import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, X, Check } from 'lucide-react';
import ReactDOM from 'react-dom';
import { api, contentApi, UpdatePostRequest, PostUpdateData, DeletePostRequest, GeneratePostByIdeaRequest } from '../services/api';
import { ImageGenerationOverlay } from './ImageGenerationOverlay';
import { PostChat } from './PostChat';
import { PostUpdateData as ChatPostUpdateData, PostLink } from '../models/ContentPlan';
import { useSwipe } from '../hooks/useSwipe';
import { ContentPlanSettings } from './ContentPlanSettings';


import WeekGenerationProgressBar from './WeekGenerationProgressBar';
import { useGeneration } from '../context/GenerationContext';
import { useAuth } from '../context/AuthContext';
import { useCustomNavigate } from '../hooks/useCustomNavigate';
import { useQueryClient } from '@tanstack/react-query';
import { useContentPlanCache } from '../hooks/useContentPlanCache';
import { KnowledgeSelector } from './KnowledgeSelector';
import { ContentModal } from './content-plan/PostChatPanel';
import { IdeaGenerationModal } from './content-plan/IdeaGenerationModal';
import { WeekGenerationModal } from './content-plan/WeekGenerationModal';
import { AddNewDayButton } from './content-plan/AddNewDayButton';
import TaskProgressBar from './TaskProgressBar';

// CSS стили для анимаций слайдов
const slideAnimationStyles = `
  @keyframes slideInFromRight {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
  @keyframes slideInFromLeft {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }
  @keyframes slideInFromTop {
    from { transform: translateY(-100%); }
    to { transform: translateY(0); }
  }
  @keyframes slideInFromBottom {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
  @keyframes progressFill {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

// Компонент для вставки стилей
const SlideAnimationStyles = () => (
  <style>{slideAnimationStyles}</style>
);

// Фразы для отображения во время генерации постов
const generatingPhrases = [
  "Анализ аудитории...",
  "Изучаю предпочтения...",
  "Смотрю референсы...",
  "Составляю план...",
  "Подбираю слова...",
  "Пишу текст...",
  "Почти готово...",
];

// Фразы для генерации по идее
const ideaGeneratingPhrases = [
  "Изучаю вашу идею...",
  "Смотрю референс...",
  "Анализирую механики...",
  "Адаптирую под вас...",
  "Создаю сценарий...",
  "Почти готово...",
];

// Компонент прогресс-бара с анимацией
interface ProgressBarProps {
  taskId?: string;
  isComplete?: boolean;
  onAnimationComplete?: () => void;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ taskId, isComplete = false, onAnimationComplete }) => {
  const [startAnimation, setStartAnimation] = useState(false);
  const [progress, setProgress] = useState(0);
  const queryClient = useQueryClient();
  
  useEffect(() => {
    if (isComplete) {
      setStartAnimation(true);
      setProgress(100);
      const timer = setTimeout(() => {
        onAnimationComplete?.();
      }, 400); // Даем время на завершение CSS-анимации
      return () => clearTimeout(timer);
    }

    if (!taskId) {
      // Fallback к старой анимации если нет taskId
      // Плавная анимация до 95% за 50 секунд.
      const timer = setTimeout(() => {
        setStartAnimation(true);
        setProgress(95);
      }, 100);

      return () => clearTimeout(timer);
    }

    const updateProgress = () => {
      const tasksData = queryClient.getQueryData(['tasks']) as any;
      if (!tasksData?.pages) return;

      const allTasks: any[] = [];
      tasksData.pages.forEach((page: any) => {
        if (page?.tasks) {
          allTasks.push(...page.tasks);
        }
      });

      const task = allTasks.find((t: any) => t.id === taskId);
      if (task) {
        if (task.status === 'completed' || task.status === 'error' || task.status === 'cancelled') {
          setProgress(100);
          return;
        }
        
        // Используем прогресс из задачи или рассчитываем по времени
        if (task.progress !== undefined) {
          setProgress(Math.min(task.progress, 95)); // максимум 95% до завершения
        } else if (task.startedAt) {
          const elapsed = (Date.now() - task.startedAt) / 1000 / 60; // в минутах
          const estimatedTime = 0.5; // 30 секунд для генерации поста
          const calculatedProgress = Math.min((elapsed / estimatedTime) * 100, 95);
          setProgress(calculatedProgress);
        }
      }
    };

    updateProgress();
    const interval = setInterval(updateProgress, 1000);
    return () => clearInterval(interval);
  }, [taskId, queryClient, isComplete, onAnimationComplete]);
  
  const durationClass = isComplete
    ? 'duration-300 ease-out'
    : !taskId
    ? 'duration-[50000ms] ease-linear'
    : 'duration-300 ease-out';
    
  return (
    <div 
      className={`absolute inset-0 bg-blue-500 dark:bg-blue-400 opacity-60 transition-transform ${durationClass} origin-left`}
      style={{
        transform: startAnimation ? `scaleX(${progress / 100})` : 'scaleX(0)'
      }}
    />
  );
};



// Функция для нормализации данных - приведение данных из PascalCase в camelCase
const normalizePlanData = (data: any): ContentPlanData | null => {
  if (!data) return null;
  
  // Создаем копию объекта для безопасного изменения
  const normalized = { ...data };
  
  // Нормализуем основные поля
  normalized.id = normalized.id || normalized.Id;
  normalized.topic = normalized.topic || normalized.Topic;
  normalized.createdAt = normalized.createdAt || normalized.CreatedAt;
  normalized.updatedAt = normalized.updatedAt || normalized.UpdatedAt;
  normalized.originalRequest = normalized.originalRequest || normalized.OriginalRequest;
  
  // Нормализуем дни - поддерживаем как новую структуру (days), так и старую (weeks)
  if (normalized.days || normalized.Days) {
    // Новая структура - прямой массив дней
    normalized.days = normalized.days || normalized.Days || [];
    if (normalized.days && Array.isArray(normalized.days)) {
      normalized.days = normalized.days.map((day: any) => {
        const normalizedDay = { ...day };
        normalizedDay.dayName = normalizedDay.dayName || normalizedDay.DayName;
        normalizedDay.date = normalizedDay.date || normalizedDay.Date;
        normalizedDay.posts = normalizedDay.posts || normalizedDay.Posts || [];
        
        if (normalizedDay.posts && Array.isArray(normalizedDay.posts)) {
          normalizedDay.posts = normalizedDay.posts.map((post: any) => {
            const normalizedPost = { ...post };
            normalizedPost.id = normalizedPost.id || normalizedPost.Id;
            normalizedPost.platform = normalizedPost.platform || normalizedPost.Platform;
            normalizedPost.format = normalizedPost.format || normalizedPost.Format;
            normalizedPost.title = normalizedPost.title || normalizedPost.Title;
            normalizedPost.idea = normalizedPost.idea || normalizedPost.Idea || '';
            normalizedPost.content = normalizedPost.content || normalizedPost.Content || normalizedPost.description || normalizedPost.Description;
            normalizedPost.time = normalizedPost.time || normalizedPost.Time;
            normalizedPost.links = normalizedPost.links || normalizedPost.Links || [];
            normalizedPost.isReelsShot = normalizedPost.isReelsShot !== undefined ? normalizedPost.isReelsShot : normalizedPost.IsReelsShot || false;
            normalizedPost.postLink = normalizedPost.postLink !== undefined ? normalizedPost.postLink : normalizedPost.PostLink;
            
            
            return normalizedPost;
          });
        }
        
        return normalizedDay;
      });
    }
  } else if (normalized.weeks || normalized.Weeks) {
    // Старая структура - преобразуем weeks в days для обратной совместимости
    const weeks = normalized.weeks || normalized.Weeks || [];
    normalized.days = [];
    
    if (weeks && Array.isArray(weeks)) {
      weeks.forEach((week: any) => {
        const normalizedWeek = { ...week };
        normalizedWeek.weekNumber = normalizedWeek.weekNumber || normalizedWeek.WeekNumber;
        normalizedWeek.days = normalizedWeek.days || normalizedWeek.Days || [];
        
        if (normalizedWeek.days && Array.isArray(normalizedWeek.days)) {
          normalizedWeek.days.forEach((day: any) => {
            const normalizedDay = { ...day };
            normalizedDay.dayName = normalizedDay.dayName || normalizedDay.DayName;
            normalizedDay.date = normalizedDay.date || normalizedDay.Date;
            normalizedDay.posts = normalizedDay.posts || normalizedDay.Posts || [];
            
            if (normalizedDay.posts && Array.isArray(normalizedDay.posts)) {
              normalizedDay.posts = normalizedDay.posts.map((post: any) => {
                const normalizedPost = { ...post };
                normalizedPost.id = normalizedPost.id || normalizedPost.Id;
                normalizedPost.platform = normalizedPost.platform || normalizedPost.Platform;
                normalizedPost.format = normalizedPost.format || normalizedPost.Format;
                normalizedPost.title = normalizedPost.title || normalizedPost.Title;
                normalizedPost.idea = normalizedPost.idea || normalizedPost.Idea || '';
                normalizedPost.content = normalizedPost.content || normalizedPost.Content || normalizedPost.description || normalizedPost.Description;
                normalizedPost.time = normalizedPost.time || normalizedPost.Time;
                normalizedPost.links = normalizedPost.links || normalizedPost.Links || [];
                normalizedPost.isReelsShot = normalizedPost.isReelsShot !== undefined ? normalizedPost.isReelsShot : normalizedPost.IsReelsShot || false;
                normalizedPost.postLink = normalizedPost.postLink !== undefined ? normalizedPost.postLink : normalizedPost.PostLink;
                return normalizedPost;
              });
            }
            
            normalized.days.push(normalizedDay);
          });
        }
      });
      
      // Сохраняем weeks для обратной совместимости
      normalized.weeks = weeks.map((week: any) => {
        const normalizedWeek = { ...week };
        normalizedWeek.weekNumber = normalizedWeek.weekNumber || normalizedWeek.WeekNumber;
        normalizedWeek.days = normalizedWeek.days || normalizedWeek.Days || [];
        return normalizedWeek;
      });
    }
  } else {
    // Нет ни days, ни weeks - инициализируем пустой массив
    normalized.days = [];
  }
  
  // Обратная совместимость - сохраняем старые поля
  normalized.durationWeeks = normalized.durationWeeks || normalized.DurationWeeks;
  normalized.socialNetworksSelected = normalized.socialNetworksSelected || normalized.SocialNetworksSelected;
  normalized.instagramFormatsUsed = normalized.instagramFormatsUsed || normalized.InstagramFormatsUsed;
  normalized.frequencyPerWeek = normalized.frequencyPerWeek || normalized.FrequencyPerWeek;
  
  return normalized as ContentPlanData;
};

// Определяем интерфейсы для данных контент-плана, которые придут от API
interface PostData {
  id?: string; // Уникальный идентификатор поста
  platform: string;
  format: string;
  title: string;
  idea: string;
  content: string;
  date?: string; // Добавлено поле для даты публикации
  aiPrompt?: string;
  coverImage?: string;
  coverPrompt?: string;
  coverStyle?: string;
  ideaPrompt?: string; // Промпт для генерации идеи
  titlePrompt?: string; // Промпт для генерации заголовка
  coverStyleId?: number;
  links?: PostLink[]; // Список ссылок поста
  isReelsShot?: boolean; // Флаг, указывающий, снят ли рилс
  postLink?: string; // Ссылка на опубликованный пост
}

interface DayData {
  dayName: string;
  date: string;
  posts: PostData[];
  isEmpty?: boolean; // Для пустых дней в календарной сетке
}

interface WeekData {
  weekNumber: number;
  days: DayData[];
}

export interface ContentPlanData {
  id?: string; // Идентификатор контент-плана
  topic: string;
  originalRequest?: {
    topic: string;
    description: string;
    services: string;
    aboutAuthor: string;
    contentPreferences?: string;
    audienceArticleId: string | null;
    authorSocialHandle?: string;
    referenceSocialHandle?: string;
    socialNetworks: {
      instagram: boolean;
      telegram: boolean;
    };
    instagramFormats: {
      reels: boolean;
      carousel: boolean;
      post: boolean;
    };
    reelsComplexity: 'simple' | 'medium' | 'complex';
    frequency: {
      reels: number;
      carousel: number;
      post: number;
      telegram: number;
    };
    duration: number;
  };
  days: DayData[]; // Прямой массив дней без группировки по неделям
  createdAt?: string;
  updatedAt?: string;
  // Сохраняем для обратной совместимости, но помечаем как deprecated
  /** @deprecated Используйте days вместо weeks */
  weeks?: WeekData[];
  /** @deprecated Используйте originalRequest.duration */
  durationWeeks?: number;
  /** @deprecated Используйте originalRequest.socialNetworks */
  socialNetworksSelected?: {
    instagram: boolean;
    telegram: boolean;
  };
  /** @deprecated Используйте originalRequest.instagramFormats */
  instagramFormatsUsed?: {
    reels: boolean;
    carousel: boolean;
    post: boolean;
  };
  /** @deprecated Используйте originalRequest.frequency */
  frequencyPerWeek?: {
    reels: number;
    carousel: number;
    post: number;
    telegram: number;
  };
  version?: number;
}

interface ContentPlanTableProps {
  planData: ContentPlanData | null;
  contentPlanId?: string;
}

// Модальное окно для работы с контентом поста
interface ContentModalProps {
  isOpen: boolean;
  post: PostData | null;
  onClose: () => void;
  onSave: (updatedPost: PostData) => void;
  contentPlanId: string | undefined;
}





// Интерфейс для модального окна выбора формата
interface FormatSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFormatSelect: (format: string) => void;
  availableFormats: string[];
  reelType?: string;
  onReelTypeChange?: (type: string | undefined) => void;
}

// Интерфейс для модального окна генерации по идее
interface IdeaGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (authorIdea?: string, referencePostUrl?: string) => void;
  isGenerating: boolean;
}



// Компонент модального окна выбора формата
const FormatSelectionModal: React.FC<FormatSelectionModalProps> = ({
  isOpen,
  onClose,
  onFormatSelect,
  availableFormats,
  reelType,
  onReelTypeChange
}) => {
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null);

  // Создаём корневой элемент для портала
  useEffect(() => {
    let root = document.getElementById('modal-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'modal-root';
      document.body.appendChild(root);
    }
    setModalRoot(root);
    
    return () => {
      if (root && !root.hasChildNodes() && root.parentElement) {
        root.parentElement.removeChild(root);
      }
    };
  }, []);

  // Блокировка прокрутки при открытом модальном окне
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Сброс локального выбора при каждом открытии
      setSelectedFormat(null);
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  if (!isOpen || !modalRoot) return null;

  // Маппинг форматов для отображения
  const formatLabels: Record<string, string> = {
    'reels': 'Reels',
    'carousel': 'Карусель', 
    'post': 'Пост'
  };

  // Функция для получения иконки формата
  const getFormatIcon = (format: string) => {
    switch (format.toLowerCase()) {
      case 'reels':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
          </svg>
        );
      case 'carousel':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <rect x="7" y="7" width="3" height="9"></rect>
            <rect x="14" y="7" width="3" height="5"></rect>
          </svg>
        );
      case 'post':
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="9" cy="9" r="2"></circle>
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
          </svg>
        );
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-0 sm:p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 w-full h-full sm:h-auto sm:max-w-lg sm:rounded-xl rounded-none shadow-2xl mx-auto flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Выберите формат поста
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Контент */}
        <div className="p-6 flex-1 overflow-y-auto">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Выберите формат и тип для генерации поста:
          </p>
          
          <div className="space-y-3">
            {availableFormats.length > 0 ? (
              availableFormats.map((format) => (
                <button
                  key={format}
                  onClick={() => setSelectedFormat(format)}
                  className={`w-full flex items-center p-4 rounded-lg transition-all duration-200 group border ${
                    selectedFormat === format
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                      : 'bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-600 border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg mr-4 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                    {getFormatIcon(format)}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {formatLabels[format.toLowerCase()] || format}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Сгенерировать {formatLabels[format.toLowerCase()]?.toLowerCase() || format.toLowerCase()}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              // Если форматы не найдены, показываем стандартные варианты
              ['reels', 'carousel', 'post'].map((format) => (
                <button
                  key={format}
                  onClick={() => setSelectedFormat(format)}
                  className={`w-full flex items-center p-4 rounded-lg transition-all duration-200 group border ${
                    selectedFormat === format
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600'
                      : 'bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600'
                  }`}
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg mr-4 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                    {getFormatIcon(format)}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {formatLabels[format]}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Сгенерировать {formatLabels[format].toLowerCase()}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Выбор типа рилса */}
          <div className="mt-6">
            <div className="text-gray-700 dark:text-gray-300 font-medium mb-2">Тип рилса</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {['Продающий', 'Экспертный', 'Развлекательный'].map((type) => {
                const isActive = reelType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => onReelTypeChange && onReelTypeChange(isActive ? undefined : type)}
                    className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600 text-gray-900 dark:text-white'
                        : 'bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
          </div>

        </div>
        {/* Футер действий — закреплён внизу модалки */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Отменить
          </button>
          <button
            type="button"
            onClick={() => selectedFormat && onFormatSelect(selectedFormat)}
            disabled={!selectedFormat || !reelType}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={!selectedFormat ? 'Выберите формат' : !reelType ? 'Выберите тип' : 'Начать генерацию'}
          >
            Начать
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, modalRoot);
};

export const ContentPlanTable: React.FC<ContentPlanTableProps> = ({ planData, contentPlanId }) => {
  
  const queryClient = useQueryClient();
  const { invalidateContentPlanData } = useContentPlanCache();
  const [selectedPost, setSelectedPost] = useState<PostData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localPlanData, setLocalPlanData] = useState<ContentPlanData | null>(planData);
  const [lastServerData, setLastServerData] = useState<ContentPlanData | null>(planData);
  
  // Удалена неиспользуемая переменная tasks из контекста генерации
  
  // Хуки для проверки подписки и навигации
  const { hasActiveSubscription } = useAuth();
  const navigate = useCustomNavigate();
  
  // Состояние для настроек контент-плана
  const [isSettingsVisible, setIsSettingsVisible] = useState(false);
  
  // Состояние для модального окна удаления
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<PostData | null>(null);
  
  // Состояние для debounce поля ссылки на пост
  const [postLinkInputValues, setPostLinkInputValues] = useState<Record<string, string>>({});
  const postLinkUpdateTimeouts = useRef<Record<string, NodeJS.Timeout>>({});
  
  // Состояние для режима редактирования ссылок на посты
  const [editingPostLinks, setEditingPostLinks] = useState<Record<string, boolean>>({});
  
  // Состояние для ошибок валидации ссылок
  const [postLinkErrors, setPostLinkErrors] = useState<Record<string, string>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Простое состояние для автоматической прокрутки при drag and drop
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const scrollSpeedRef = useRef<number>(10);
  const scrollDirectionRef = useRef<'up' | 'down' | null>(null);
  
  // Функция для остановки автоматической прокрутки
  const stopAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
    setIsAutoScrolling(false);
    scrollDirectionRef.current = null;
  }, []);
  
  // Простая функция для обработки автоматической прокрутки во время drag
  const handleAutoScroll = useCallback((clientY: number) => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    
    const viewportHeight = window.innerHeight;
    const topZone = 120; // Верхние 120 пикселей
    const bottomZone = viewportHeight - 120; // Нижние 120 пикселей
    
    let shouldScroll = false;
    let scrollDirection: 'up' | 'down' | null = null;
    let scrollSpeed = 10; // базовая скорость
    
    // Определяем зону прокрутки и скорость
    if (clientY <= topZone) {
      shouldScroll = true;
      scrollDirection = 'up';
      // Чем ближе к краю, тем быстрее прокрутка (в пределах 120px зоны)
      const distanceFromTop = clientY / topZone; // 0-1, где 0 = край экрана
      scrollSpeed = Math.max(5, 18 * (1 - distanceFromTop)); // от 5 до 18
    } else if (clientY >= bottomZone) {
      shouldScroll = true;
      scrollDirection = 'down';
      // Чем ближе к краю, тем быстрее прокрутка (в пределах 120px зоны)
      const distanceFromBottom = (viewportHeight - clientY) / 120; // 0-1
      scrollSpeed = Math.max(5, 18 * (1 - distanceFromBottom)); // от 5 до 18
    }
    
    // Сохраняем текущие значения в ref'ы
    scrollSpeedRef.current = scrollSpeed;
    scrollDirectionRef.current = scrollDirection;
    
    if (shouldScroll && scrollDirection) {
      // Запускаем интервал только если его еще нет
      if (!autoScrollIntervalRef.current) {
        
        setIsAutoScrolling(true);
        
        // Запускаем интервал прокрутки с ref'ами для актуальных значений
        autoScrollIntervalRef.current = setInterval(() => {
          const currentSpeed = scrollSpeedRef.current;
          const currentDirection = scrollDirectionRef.current;
          
          if (!currentDirection) {
            return;
          }
          
          // Проверяем, можно ли прокручивать дальше
          const canScrollUp = container.scrollTop > 0;
          const canScrollDown = container.scrollTop < 
            (container.scrollHeight - container.clientHeight);
          
          if (currentDirection === 'up' && !canScrollUp) {
            return;
          }
          
          if (currentDirection === 'down' && !canScrollDown) {
            return;
          }
          
          const scrollAmount = currentDirection === 'up' ? -currentSpeed : currentSpeed;
          container.scrollBy(0, scrollAmount);
        }, 50); // Увеличиваем интервал до 50ms для более плавной прокрутки
      } else {
        // Просто обновляем скорость и направление в существующем интервале
      }
    } else {
      // Останавливаем автопрокрутку
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
        setIsAutoScrolling(false);
        scrollDirectionRef.current = null;
      }
    }
  }, [isAutoScrolling]);
  
  // Функция валидации ссылок в зависимости от платформы и типа поста
  const validatePostLink = useCallback((url: string, platform: string, format?: string): { isValid: boolean; message?: string } => {
    if (!url.trim()) {
      return { isValid: true }; // Пустая ссылка допустима
    }
    
    // Проверяем базовую структуру URL
    try {
      new URL(url);
    } catch {
      return { isValid: false, message: 'Некорректный формат URL' };
    }
    
    const lowerPlatform = platform.toLowerCase();
    const lowerUrl = url.toLowerCase();
    
    if (lowerPlatform === 'instagram') {
      const lowerFormat = format?.toLowerCase();
      
      // Проверяем соответствие типа ссылки типу поста
      if (lowerFormat === 'reels') {
        // Для рилсов принимаем только ссылки на рилсы
        const reelRegex = /^https?:\/\/(www\.)?instagram\.com\/reel\/[a-zA-Z0-9_-]+\/?.*$/;
        if (!reelRegex.test(lowerUrl)) {
          return { 
            isValid: false, 
            message: 'Для рилса ссылка должна быть в формате instagram.com/reel/...' 
          };
        }
      } else if (lowerFormat === 'post' || lowerFormat === 'carousel') {
        // Для постов и каруселей принимаем только ссылки на посты
        const postRegex = /^https?:\/\/(www\.)?instagram\.com\/p\/[a-zA-Z0-9_-]+\/?.*$/;
        if (!postRegex.test(lowerUrl)) {
          return { 
            isValid: false, 
            message: 'Для поста/карусели ссылка должна быть в формате instagram.com/p/...' 
          };
        }
      } else {
        // Для неизвестного формата используем общую проверку
        const instagramRegex = /^https?:\/\/(www\.)?instagram\.com\/(p|reel)\/[a-zA-Z0-9_-]+\/?.*$/;
        if (!instagramRegex.test(lowerUrl)) {
          return { 
            isValid: false, 
            message: 'Ссылка должна быть в формате instagram.com/p/... или instagram.com/reel/...' 
          };
        }
      }
    } else if (lowerPlatform === 'telegram') {
      // Telegram поддерживает:
      // - https://t.me/channel_name/post_id
      // - https://telegram.me/channel_name/post_id
      // - https://t.me/s/channel_name/post_id (для публичных каналов)
      const telegramRegex = /^https?:\/\/(t\.me|telegram\.me)(\/s)?\/[a-zA-Z0-9_]+\/\d+\/?.*$/;
      
      if (!telegramRegex.test(lowerUrl)) {
        return { 
          isValid: false, 
          message: 'Ссылка должна быть в формате t.me/канал/номер_поста' 
        };
      }
    }
    
    return { isValid: true };
  }, []);

  // Функция для генерации placeholder в зависимости от платформы и формата
  const getPostLinkPlaceholder = useCallback((platform: string, format?: string): string => {
    const lowerPlatform = platform.toLowerCase();
    const lowerFormat = format?.toLowerCase();
    
    if (lowerPlatform === 'instagram') {
      if (lowerFormat === 'reels') {
        return 'Ссылка на рилс (Instagram)';
      } else if (lowerFormat === 'post' || lowerFormat === 'carousel') {
        return 'Ссылка на пост (Instagram)';
      }
      return 'Ссылка на пост/рилс (Instagram)';
    }
    
    return `Ссылка на пост (${platform})`;
  }, []);
  
  // Функция для обновления ссылки на пост с debounce и валидацией
  const handlePostLinkChange = useCallback((post: PostData, value: string) => {
    const postId = post.id!;
    
    // Обновляем локальное значение немедленно
    setPostLinkInputValues(prev => ({
      ...prev,
      [postId]: value
    }));
    
    // Валидируем ссылку в зависимости от платформы
    const validation = validatePostLink(value, post.platform, post.format);
    
    // Обновляем ошибки валидации
    setPostLinkErrors(prev => {
      const newErrors = { ...prev };
      if (validation.isValid) {
        delete newErrors[postId];
      } else {
        newErrors[postId] = validation.message || 'Неверный формат ссылки';
      }
      return newErrors;
    });
    
    // Очищаем предыдущий таймер если он есть
    if (postLinkUpdateTimeouts.current[postId]) {
      clearTimeout(postLinkUpdateTimeouts.current[postId]);
    }
    
    // Отправляем запрос только если валидация прошла успешно
    if (validation.isValid) {
      // Устанавливаем новый таймер на 500ms
      postLinkUpdateTimeouts.current[postId] = setTimeout(() => {
        const updatedPost = {
          ...post,
          postLink: value || undefined
        };
        
        // Сначала optimistic update
        updateLocalPost(updatedPost);
        // Затем API запрос
        handleSavePost(updatedPost, true);
        
        // Очищаем значение из локального state только после успешного обновления
        setTimeout(() => {
          setPostLinkInputValues(prev => {
            const newValues = { ...prev };
            delete newValues[postId];
            return newValues;
          });
        }, 100); // Небольшая задержка чтобы updateLocalPost успел обработаться
        
        // Очищаем таймер
        delete postLinkUpdateTimeouts.current[postId];
      }, 500);
    } else {
    }
  }, [validatePostLink]);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Состояние для генерации новой недели
  const [isWeekGenerationModalOpen, setIsWeekGenerationModalOpen] = useState(false);
  // Week generation state from context
  const { 
    isGeneratingWeek, 
    weekGenerationStartTime, 
    weekGenerationTaskId, 
    setWeekGenerationState,
    cancelTask,
    tasksSignalRServiceRef
  } = useGeneration();
  const [weekGenerationProgress, setWeekGenerationProgress] = useState<number | undefined>(undefined);

  // Состояние для отслеживания генерации постов через контекст
  const [postGenerationTasks, setPostGenerationTasks] = useState<Record<string, string>>({});
  const [completingPostGenerationForDate, setCompletingPostGenerationForDate] = useState<string | null>(null);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [currentIdeaPhraseIndex, setCurrentIdeaPhraseIndex] = useState(0);

  // Эффект для отслеживания изменений в задачах генерации постов через SignalR
  useEffect(() => {
    const signalRService = tasksSignalRServiceRef?.current;
    
    if (!signalRService) return;
    
    // Подписываемся на события завершения задач генерации постов
    const unsubscribeCompleted = signalRService.onTaskCompleted((task: any) => {
      const taskId = task.id || task.taskId;
      
      // Ищем эту задачу среди наших задач генерации постов
      setPostGenerationTasks(prev => {
        const newState = { ...prev };
        const dateForTask = Object.keys(newState).find(date => newState[date] === taskId);
        
        if (dateForTask) {
          console.log('✅ [POST] Генерация поста завершена через SignalR для', dateForTask);
          delete newState[dateForTask];
          
          // Инвалидируем кэш для обновления данных
          if (contentPlanId) {
            setTimeout(() => {
              invalidateContentPlanData(contentPlanId);
            }, 500);
          }
        }
        
        return newState;
      });
    });
    
    const unsubscribeError = signalRService.onTaskError((task: any) => {
      const taskId = task.id || task.taskId;
      
      setPostGenerationTasks(prev => {
        const newState = { ...prev };
        const dateForTask = Object.keys(newState).find(date => newState[date] === taskId);
        
        if (dateForTask) {
          console.log('❌ [POST] Ошибка генерации поста через SignalR для', dateForTask);
          delete newState[dateForTask];
        }
        
        return newState;
      });
    });
    
    const unsubscribeCancelled = signalRService.onTaskCancelled((task: any) => {
      const taskId = task.id || task.taskId;
      
      setPostGenerationTasks(prev => {
        const newState = { ...prev };
        const dateForTask = Object.keys(newState).find(date => newState[date] === taskId);
        
        if (dateForTask) {
          console.log('🚫 [POST] Генерация поста отменена через SignalR для', dateForTask);
          delete newState[dateForTask];
        }
        
        return newState;
      });
    });
    
    return () => {
      unsubscribeCompleted();
      unsubscribeError();
      unsubscribeCancelled();
    };
  }, [tasksSignalRServiceRef, contentPlanId, invalidateContentPlanData]);

  
  // Состояние для выбора формата поста
  const [isFormatSelectionModalOpen, setIsFormatSelectionModalOpen] = useState(false);
  const [selectedDateForGeneration, setSelectedDateForGeneration] = useState<string | null>(null);
  const [formatSelectionReelType, setFormatSelectionReelType] = useState<string | undefined>('Экспертный');
  
  // Состояние для модального окна генерации по идее
  const [isIdeaGenerationModalOpen, setIsIdeaGenerationModalOpen] = useState(false);
  // Дата, для которой сейчас идёт генерация по идее (локальная анимация)
  const [generatingIdeaForDate, setGeneratingIdeaForDate] = useState<string | null>(null);
  
  // Состояние для drag & drop
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPostId, setDraggedPostId] = useState<string | null>(null);
  const [draggedFromDate, setDraggedFromDate] = useState<string | null>(null);
  const [dropTargetDate, setDropTargetDate] = useState<string | null>(null);
  const [isMovingPost, setIsMovingPost] = useState(false);
  const dropTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [generatingPostForDate, setGeneratingPostForDate] = useState<string | null>(null);
  const [dragDropVersion, setDragDropVersion] = useState(0); // Для принудительного обновления UI
  const [isDragDropCompleted, setIsDragDropCompleted] = useState(false); // Флаг завершения drag & drop
  const pendingMoveRef = useRef<{ postId: string; targetDate: string; timestamp: number } | null>(null); // Ожидаем подтверждение от сервера
  const pendingMoveTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Тайм-аут для очистки pendingMove
  
  // Состояние для сортировки
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const saved = localStorage.getItem(`contentPlan_${contentPlanId}_sortOrder`);
    return (saved as 'asc' | 'desc') || 'asc';
  });
  // Состояние для скрытия прошедших дней
  const [hidePastDays, setHidePastDays] = useState<boolean>(true);
  
  // Проверяем, идет ли генерация постов
  const isAnyPostGenerating = useMemo(() => {
    return (
      generatingPostForDate !== null ||
      generatingIdeaForDate !== null ||
      Object.values(postGenerationTasks).some(taskId => !!taskId)
    );
  }, [generatingPostForDate, generatingIdeaForDate, postGenerationTasks]);

  // Эффект для смены фраз во время генерации
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isAnyPostGenerating) {
      interval = setInterval(() => {
        setCurrentPhraseIndex(prevIndex => {
          // Останавливаемся на последней фразе, не идем по второму кругу
          if (prevIndex < generatingPhrases.length - 1) {
            return prevIndex + 1;
          }
          return prevIndex; // Остаемся на последней фразе
        });
      }, 7000); // Смена фразы каждые 7 секунд (50 сек / 7 фраз)
    } else {
      setCurrentPhraseIndex(0); // Сброс при отсутствии генерации
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAnyPostGenerating]);

  // Эффект для смены фраз во время генерации по идее
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (generatingIdeaForDate) {
      interval = setInterval(() => {
        setCurrentIdeaPhraseIndex(prevIndex => {
          // Останавливаемся на последней фразе, не идем по второму кругу
          if (prevIndex < ideaGeneratingPhrases.length - 1) {
            return prevIndex + 1;
          }
          return prevIndex; // Остаемся на последней фразе
        });
      }, 12000); // Смена фразы каждые 12 секунд
    } else {
      setCurrentIdeaPhraseIndex(0); // Сброс при отсутствии генерации
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [generatingIdeaForDate]);
  
  useEffect(() => {
    if (!isGeneratingWeek || !weekGenerationTaskId) {
        setWeekGenerationProgress(undefined);
        return;
    }

    const updateProgress = () => {
        const tasksData = queryClient.getQueryData(['tasks']) as any;
        if (!tasksData?.pages) return;

        const allTasks: any[] = [];
        tasksData.pages.forEach((page: any) => {
            if (page?.tasks) {
                allTasks.push(...page.tasks);
            }
        });

        const task = allTasks.find((t: any) => t.id === weekGenerationTaskId);
        if (task && task.progress !== undefined) {
            setWeekGenerationProgress(task.progress);
        }
    };

    const interval = setInterval(updateProgress, 1000);
    return () => clearInterval(interval);
}, [isGeneratingWeek, weekGenerationTaskId, queryClient]);

  // Обновляем локальные данные при изменении входящих данных только если это новые данные с сервера
  React.useEffect(() => {
    
    // Инициализация при первой загрузке
    if (!localPlanData && planData) {
      setLocalPlanData(planData);
      setLastServerData(planData);
      return;
    }
    
    if (planData && pendingMoveRef.current && localPlanData) {
      const { postId, targetDate, timestamp } = pendingMoveRef.current;
      
      // Проверяем тайм-аут (30 секунд максимум ожидания для медленного сервера)
      const now = Date.now();
      if (now - timestamp > 30000) {
        pendingMoveRef.current = null;
        if (pendingMoveTimeoutRef.current) {
          clearTimeout(pendingMoveTimeoutRef.current);
          pendingMoveTimeoutRef.current = null;
        }
        // Продолжаем обычную синхронизацию
      } else {
        // 🔧 Нормализуем targetDate для корректного сравнения
        // ВАЖНО: targetDate уже в формате YYYY-MM-DD из pendingMoveRef
        const normalizedTargetDate = targetDate;
        
        const serverPostDate = (() => {
          if (planData.days) {
            for (const d of planData.days) {
              if (d.posts.some(p => p.id === postId)) {
                return normalizeDate(d.date);
              }
            }
          } else if (planData.weeks) {
            for (const w of planData.weeks) {
              for (const d of w.days) {
                if (d.posts.some(p => p.id === postId)) {
                  return normalizeDate(d.date);
                }
              }
            }
          }
          return null;
        })();


        // Если сервер подтвердил новую дату, очищаем флаг ожидания и обновляем данные
        if (serverPostDate === normalizedTargetDate) {
          pendingMoveRef.current = null;
          if (pendingMoveTimeoutRef.current) {
            clearTimeout(pendingMoveTimeoutRef.current);
            pendingMoveTimeoutRef.current = null;
          }
          
          // Теперь безопасно обновляем локальные данные с сервера
          const mergedData = mergeServerDataWithLocalFields(planData, localPlanData);
          setLocalPlanData(mergedData);
          setLastServerData(planData);
          return;
        } else if (serverPostDate) {
          // Сервер ещё не подтвердил изменения, пропускаем синхронизацию
          const waitingTime = now - timestamp;

          
          // 🔄 Если ждём уже больше 5 секунд, запрашиваем свежие данные принудительно
          if (waitingTime > 5000 && waitingTime % 5000 < 1000) { // каждые 5 секунд
            setTimeout(async () => {
              try {
                const freshData = await api.get(`/content-plan/plan/${contentPlanId}`);
                if (freshData.data) {
                  queryClient.setQueryData(['contentPlan', contentPlanId], freshData.data);
                }
              } catch (error) {
                console.error('❌ Ошибка принудительной загрузки:', error);
              }
            }, 100);
          }
          
          return;
        }
      }
    }

    // Проверяем, изменились ли данные на сервере (например, обновились версия или дата изменения)
    if (planData && lastServerData) {
      const serverDataChanged = 
        planData.version !== lastServerData.version ||
        planData.updatedAt !== lastServerData.updatedAt ||
        JSON.stringify(planData) !== JSON.stringify(lastServerData);
        
      if (serverDataChanged) {
        console.log('📥 [DATA] Обнаружены изменения с сервера, обновляем локальные данные');
        // Умное слияние: сохраняем локальные поля isReelsShot и postLink
        const mergedData = mergeServerDataWithLocalFields(planData, localPlanData!);
        setLocalPlanData(mergedData);
        setLastServerData(planData);
      } else {
        console.log('📊 [DATA] Данные с сервера не изменились');
      }
    } else if (planData && !lastServerData) {
      // Новые данные пришли с сервера
      console.log('🆕 [DATA] Первая загрузка данных с сервера');
      setLocalPlanData(planData);
      setLastServerData(planData);
    }
  }, [planData, isDragging, isMovingPost, isGeneratingWeek]); // Добавляем isMovingPost и isGeneratingWeek в зависимости

  // Очистка таймаутов при размонтировании
  useEffect(() => {
    return () => {
      if (dropTimeoutRef.current) {
        clearTimeout(dropTimeoutRef.current);
      }
      
      // Очищаем все таймеры debounce для ссылок на постов
      Object.values(postLinkUpdateTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      postLinkUpdateTimeouts.current = {};
      
      // Очищаем тайм-аут ожидания подтверждения от сервера
      if (pendingMoveTimeoutRef.current) {
        clearTimeout(pendingMoveTimeoutRef.current);
        pendingMoveTimeoutRef.current = null;
      }
      
      // Останавливаем автоматическую прокрутку при размонтировании
      stopAutoScroll();
    };
  }, [stopAutoScroll]);

  // Сохранение настройки сортировки в localStorage
  useEffect(() => {
    if (contentPlanId) {
      localStorage.setItem(`contentPlan_${contentPlanId}_sortOrder`, sortOrder);
    }
  }, [sortOrder, contentPlanId]);

  // Больше не сохраняем состояние скрытия прошедших дней

  // Сброс видимости настроек при смене contentPlanId
  useEffect(() => {
    setIsSettingsVisible(false);
  }, [contentPlanId]);
  
  // Функция для умного слияния данных с сервера с локальными полями
  const mergeServerDataWithLocalFields = (serverData: ContentPlanData, localData: ContentPlanData): ContentPlanData => {
    if (!localData) return serverData;
    
    const merged = { ...serverData };
    
    // Создаем карту локальных постов для быстрого поиска
    const localPostsMap = new Map<string, PostData>();
    
    // Собираем все локальные посты
    if (localData.days) {
      localData.days.forEach(day => {
        day.posts.forEach(post => {
          if (post.id) {
            localPostsMap.set(post.id, post);
          }
        });
      });
    } else if (localData.weeks) {
      localData.weeks.forEach(week => {
        week.days.forEach(day => {
          day.posts.forEach(post => {
            if (post.id) {
              localPostsMap.set(post.id, post);
            }
          });
        });
      });
    }
    
    // Применяем слияние для структуры days
    if (merged.days) {
      merged.days = merged.days.map(day => ({
        ...day,
        posts: day.posts.map(serverPost => {
          const localPost = localPostsMap.get(serverPost.id || '');
          if (localPost) {
            return {
              ...serverPost,
              isReelsShot: localPost.isReelsShot,
              postLink: localPost.postLink
            };
          }
          return serverPost;
        })
      }));
    }
    
    // Применяем слияние для структуры weeks (обратная совместимость)
    if (merged.weeks) {
      merged.weeks = merged.weeks.map(week => ({
        ...week,
        days: week.days.map(day => ({
          ...day,
          posts: day.posts.map(serverPost => {
            const localPost = localPostsMap.get(serverPost.id || '');
            if (localPost) {
                          return {
              ...serverPost,
              isReelsShot: localPost.isReelsShot,
              postLink: localPost.postLink
            };
            }
            return serverPost;
          })
        }))
      }));
    }
    
    return merged;
  };

  // Функция для обновления конкретного поста в локальных данных
  const updateLocalPost = (updatedPost: PostData) => {
    
    if (!localPlanData || !updatedPost.id) {
      return;
    }
    
    // Обновляем для новой структуры с days
    if (localPlanData.days) {
      const newPlanData = {
        ...localPlanData,
        days: localPlanData.days.map(day => ({
          ...day,
          posts: day.posts.map(post => {
            if (post.id === updatedPost.id) {
              return {
                id: updatedPost.id,
                platform: updatedPost.platform,
                format: updatedPost.format,
                title: updatedPost.title,
                idea: updatedPost.idea,
                content: updatedPost.content,
                date: updatedPost.date,
                coverImage: updatedPost.coverImage,
                coverStyle: updatedPost.coverStyle,
                coverStyleId: updatedPost.coverStyleId,
                isReelsShot: updatedPost.isReelsShot,
                postLink: updatedPost.postLink,
                links: updatedPost.links
              };
            }
            return { ...post };
          })
        }))
      };
      setLocalPlanData(newPlanData);
    } else if (localPlanData.weeks) {
      // Обратная совместимость со старой структурой weeks
      const newPlanData = {
        ...localPlanData,
        weeks: localPlanData.weeks.map(week => ({
          ...week,
          days: week.days.map(day => ({
            ...day,
            posts: day.posts.map(post => {
              if (post.id === updatedPost.id) {
                return {
                  id: updatedPost.id,
                  platform: updatedPost.platform,
                  format: updatedPost.format,
                  title: updatedPost.title,
                  idea: updatedPost.idea,
                  content: updatedPost.content,
                  date: updatedPost.date,
                  coverImage: updatedPost.coverImage,
                  coverStyle: updatedPost.coverStyle,
                  coverStyleId: updatedPost.coverStyleId,
                  isReelsShot: updatedPost.isReelsShot,
                  postLink: updatedPost.postLink,
                  links: updatedPost.links
                };
              }
              return { ...post };
            })
          }))
        }))
      };
      setLocalPlanData(newPlanData);
    }
  };

  // Функция для добавления нового поста в локальные данные
  const addLocalPost = (newPost: PostData, dayDate: string) => {
    
    if (!localPlanData) {
      return;
    }
    
    
    let postAdded = false;
    
    // Функция для сравнения дат в разных форматах
    const datesMatch = (date1: string, date2: string): boolean => {
      
      if (date1 === date2) {
        return true;
      }
      
      // Пробуем парсить обе даты и сравнить
      const parseDate = (dateStr: string): Date | null => {
        try {
          // Попробуем разные форматы
          if (dateStr.includes('.')) {
            const [day, month, year] = dateStr.split('.');
            const parsed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            return parsed;
          } else if (dateStr.includes('-')) {
            const parsed = new Date(dateStr + 'T00:00:00.000Z');
            return parsed;
          }
          const parsed = new Date(dateStr);
          return parsed;
        } catch (error) {
          return null;
        }
      };
      
      const parsedDate1 = parseDate(date1);
      const parsedDate2 = parseDate(date2);
      
      if (parsedDate1 && parsedDate2) {
        // Сравниваем только дату (без времени)
        const date1Only = new Date(parsedDate1.getFullYear(), parsedDate1.getMonth(), parsedDate1.getDate());
        const date2Only = new Date(parsedDate2.getFullYear(), parsedDate2.getMonth(), parsedDate2.getDate());
        
        const isMatch = date1Only.getTime() === date2Only.getTime();
        return isMatch;
      }
      
      return false;
    };
    
    
    // Создаем полностью новый объект данных
    const newPlanData = {
      ...localPlanData,
      weeks: localPlanData.weeks?.map((week, weekIndex) => {
        return {
          ...week,
          days: week.days.map((day, dayIndex) => {
            const matches = datesMatch(day.date, dayDate);
            
            if (matches) {
              postAdded = true;
              return {
                ...day,
                posts: [
                  ...day.posts.map(post => ({ ...post })), // Копируем существующие посты
                  {
                    // Создаем полностью новый объект поста
                    id: newPost.id || `generated_${Date.now()}`,
                    platform: newPost.platform,
                    format: newPost.format,
                    title: newPost.title,
                    idea: newPost.idea,
                    content: newPost.content,
                    date: newPost.date || dayDate,
                    coverImage: newPost.coverImage,
                    coverStyle: newPost.coverStyle,
                    coverStyleId: newPost.coverStyleId
                  }
                ]
              };
            }
            return { ...day, posts: day.posts.map(post => ({ ...post })) };
          })
        };
      })
    };
    
    if (!postAdded) {
      
      // Если день не найден, попробуем создать новый день
      const newDay = {
        date: dayDate,
        dayName: getDayNameFromDate(dayDate),
        posts: [{
          id: newPost.id || `generated_${Date.now()}`,
          platform: newPost.platform,
          format: newPost.format,
          title: newPost.title,
          idea: newPost.idea,
          content: newPost.content,
          date: newPost.date || dayDate,
          coverImage: newPost.coverImage,
          coverStyle: newPost.coverStyle,
          coverStyleId: newPost.coverStyleId
        }]
      };
      
      // Добавляем новый день к последней неделе
      if (localPlanData.weeks && localPlanData.weeks.length > 0) {
        const updatedPlanWithNewDay = {
          ...localPlanData,
          weeks: localPlanData.weeks.map((week, index) => {
            if (index === localPlanData.weeks!.length - 1) {
              return {
                ...week,
                days: [...week.days, newDay]
              };
            }
            return week;
          })
        };
        setLocalPlanData(updatedPlanWithNewDay);
        postAdded = true;
      }
    } else {
      setLocalPlanData(newPlanData);
    }
    
  };
  
  // Вспомогательная функция для получения названия дня недели
  const getDayNameFromDate = (dateStr: string): string => {
    try {
      let date: Date;
      if (dateStr.includes('.')) {
        const [day, month, year] = dateStr.split('.');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        date = new Date(dateStr);
      }
      
      const dayNames = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
      return dayNames[date.getDay()];
    } catch {
      return 'Неизвестный день';
    }
  };
  
  // Функция для удаления поста из локальных данных
  const removeLocalPost = (postId: string) => {
    
    if (!localPlanData || !postId) {
      return;
    }
    
    // Поддержка новой структуры с days
    if (localPlanData.days) {
      const newPlanData = {
        ...localPlanData,
        days: localPlanData.days.map(day => ({
          ...day,
          posts: day.posts.filter(post => post.id !== postId).map(post => ({ ...post }))
        }))
      };
      setLocalPlanData(newPlanData);
    } else if (localPlanData.weeks) {
      // Обратная совместимость со старой структурой weeks
      const newPlanData = {
        ...localPlanData,
        weeks: localPlanData.weeks.map(week => ({
          ...week,
          days: week.days.map(day => ({
            ...day,
            posts: day.posts.filter(post => post.id !== postId).map(post => ({ ...post }))
          }))
        }))
      };
      setLocalPlanData(newPlanData);
    }
  };
  
  // Нормализуем данные чтобы учесть различия в именовании полей с кэшированием
  // ВАЖНО: хук должен быть вызван до любых условных возвратов
  const normalizedPlanData = useMemo(() => {
    if (!localPlanData) {
      return null;
    }
    const normalized = normalizePlanData(localPlanData);
    
    if (normalized?.days) {
      const totalPosts = normalized.days.reduce((total, day) => total + day.posts.length, 0);
    }
    
    return normalized;
  }, [localPlanData]);
  
  // Функция для получения полного списка всех дней, включая пустые промежутки
  // ВАЖНО: Должна быть объявлена ДО условных возвратов, чтобы соблюдать правила хуков React
  const getAllDays = useCallback(() => {

    
    // Приоритет локальным данным для drag & drop
    const currentDays = localPlanData?.days || normalizedPlanData?.days || [];
    const currentWeeks = localPlanData?.weeks || normalizedPlanData?.weeks || [];
    
    let allExistingDays: DayData[] = [];
    
    if (currentDays.length > 0) {
      // Новая структура - используем массив дней напрямую
      allExistingDays = [...currentDays];
      
      const totalPosts = currentDays.reduce((total, day) => total + (day.posts?.length || 0), 0);
    
    } else if (currentWeeks.length > 0) {
      // Обратная совместимость - используем weeks
      allExistingDays = currentWeeks.flatMap(week => week.days || []);
      
      const totalDays = currentWeeks.reduce((total, week) => total + (week.days?.length || 0), 0);
      const totalPosts = currentWeeks.reduce((total, week) => 
        total + week.days.reduce((dayTotal, day) => dayTotal + (day.posts?.length || 0), 0), 0
      );
      
      // Отладочная информация о днях в неделях
      currentWeeks.forEach((week, weekIndex) => {
        week.days?.forEach((day, dayIndex) => {

        });
      });
    }
    
    if (allExistingDays.length === 0) {
      return [];
    }

    // Более надежный парсер дат, который поддерживает YYYY-MM-DD и "9 февраля"
    const parseDate = (dateStr: string): Date | null => {
        if (!dateStr) return null;

        // NEW: Handle dd.MM.yyyy format
        const ddMMyyyyMatch = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (ddMMyyyyMatch) {
            const day = parseInt(ddMMyyyyMatch[1], 10);
            const month = parseInt(ddMMyyyyMatch[2], 10);
            const year = parseInt(ddMMyyyyMatch[3], 10);
            return new Date(Date.UTC(year, month - 1, day));
        }

        // Проверяем формат YYYY-MM-DD и создаем дату в UTC, чтобы избежать проблем с часовыми поясами
        const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            const year = parseInt(match[1], 10);
            const month = parseInt(match[2], 10);
            const day = parseInt(match[3], 10);
            // Месяцы в JS начинаются с 0, поэтому month - 1
            return new Date(Date.UTC(year, month - 1, day));
        }

        // Запасной вариант для формата "9 февраля"
        const monthNames = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
            'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
        const currentYear = new Date().getFullYear();
        const parts = dateStr.trim().split(' ');
        if (parts.length === 2) {
            const day = parseInt(parts[0], 10);
            const monthIndex = monthNames.findIndex(m => m.startsWith(parts[1].toLowerCase()));
            if (!isNaN(day) && monthIndex !== -1) {
                return new Date(Date.UTC(currentYear, monthIndex, day));
            }
        }
        
        // Общий запасной вариант, который пытается распарсить строку как есть
        const genericDate = new Date(dateStr);
        if (!isNaN(genericDate.getTime())) {
            return genericDate;
        }

        return null;
    };
    
    // Функция для форматирования даты в формат dd.MM.yyyy с использованием UTC
    const formatDate = (date: Date): string => {
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${day}.${month}.${year}`;
    };

    const dayNames = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
    const daysWithParsedDates = allExistingDays
      .map(day => ({
        ...day,
        parsedDate: parseDate(day.date),
        // Убеждаемся, что у каждого дня есть dayName
        dayName: day.dayName || (() => {
          try {
            const date = parseDate(day.date);
            if (date) {
              return dayNames[date.getDay()];
            }
          } catch {}
          return '';
        })()
      }))
      .filter(day => day.parsedDate !== null) as (DayData & { parsedDate: Date })[];

    if (daysWithParsedDates.length === 0) {
      // Если не удалось распарсить ни одной даты, возвращаем как есть
      return allExistingDays;
    }

    daysWithParsedDates.sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

    const startDate = daysWithParsedDates[0].parsedDate;
    const endDate = daysWithParsedDates[daysWithParsedDates.length - 1].parsedDate;
    
    const allDaysInRange: DayData[] = [];
    const currentDate = new Date(startDate);

    const existingDaysMap = new Map(
        daysWithParsedDates.map(day => [formatDate(day.parsedDate), day])
    );
    
    while (currentDate <= endDate) {
      const formattedDate = formatDate(currentDate);
      if (existingDaysMap.has(formattedDate)) {
        const existingDay = existingDaysMap.get(formattedDate)!;
        allDaysInRange.push({
            ...existingDay,
            date: formatDate(existingDay.parsedDate), // Убедимся, что дата отформатирована
            dayName: existingDay.dayName || dayNames[currentDate.getDay()] // Сохраняем существующий dayName или генерируем новый
        });
      } else {
        allDaysInRange.push({
          date: formattedDate,
          dayName: dayNames[currentDate.getDay()],
          posts: [],
          isEmpty: true,
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Применяем сортировку в зависимости от выбранного порядка
    if (sortOrder === 'desc') {
      allDaysInRange.reverse();
    }

    // Финальная проверка: убеждаемся, что у всех дней есть dayName
    const finalDays = allDaysInRange.map(day => {
      if (!day.dayName) {
        try {
          const date = parseDate(day.date);
          if (date) {
            const generatedDayName = dayNames[date.getDay()];
            return { ...day, dayName: generatedDayName };
          }
        } catch {}
        return { ...day, dayName: 'Неизвестный день' };
      }
      return day;
    });

    return finalDays;
  }, [normalizedPlanData, localPlanData, sortOrder]);

  // Получаем дни с мемоизацией для предотвращения ненужных перерендеров
  const allDays = useMemo(() => {
    const days = getAllDays();
    return days;
  }, [getAllDays, localPlanData, dragDropVersion]); // Добавляем dragDropVersion в зависимости

  // Функция для нормализации формата даты
  // ВАЖНО: Должна быть объявлена ДО использования в хуках
  const normalizeDate = (dateStr: string): string => {
    if (!dateStr) return dateStr;
    
    // Если дата в формате DD.MM.YYYY, конвертируем в YYYY-MM-DD
    if (dateStr.includes('.')) {
      const [day, month, year] = dateStr.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Если дата уже в формате YYYY-MM-DD, возвращаем как есть
    return dateStr;
  };

  // Преобразование строки даты к UTC полуночи (ms)
  // ВАЖНО: Должна быть объявлена ДО использования в хуках
  const parseDateToUtcMs = (dateStr: string): number | null => {
    if (!dateStr) return null;
    try {
      // dd.MM.yyyy
      const ddMMyyyy = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (ddMMyyyy) {
        const day = parseInt(ddMMyyyy[1], 10);
        const month = parseInt(ddMMyyyy[2], 10) - 1;
        const year = parseInt(ddMMyyyy[3], 10);
        return Date.UTC(year, month, day);
      }
      // yyyy-MM-dd
      const yyyymmdd = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (yyyymmdd) {
        const year = parseInt(yyyymmdd[1], 10);
        const month = parseInt(yyyymmdd[2], 10) - 1;
        const day = parseInt(yyyymmdd[3], 10);
        return Date.UTC(year, month, day);
      }
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      }
      return null;
    } catch {
      return null;
    }
  };

  // Итоговый список дней с учетом фильтра «Скрыть прошедшие»
  const daysToRender = useMemo(() => {
    if (!hidePastDays) return allDays;
    const now = new Date();
    const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return allDays.filter(d => {
      const ms = parseDateToUtcMs(d.date);
      if (ms === null) return true; // если дату не распознали — не скрываем
      return ms >= todayUtc; // скрываем только строго прошедшие дни
    });
  }, [hidePastDays, allDays]);

  // Получение доступных форматов для Instagram
  const getAvailableInstagramFormats = useMemo(() => {
    // Всегда возвращаем все доступные форматы Instagram
    return ['reels', 'carousel', 'post'];
  }, []);

  // Глобальный обработчик drag для автоматической прокрутки
  // ВАЖНО: Должен быть объявлен ДО условных возвратов
  useEffect(() => {
    if (!isDragging) return;
    
    const handleGlobalDragOver = (e: DragEvent) => {
      if (!isDragging) return;
      
      // Получаем позицию курсора относительно viewport
      const clientY = e.clientY;
      
      // Обрабатываем автоматическую прокрутку
      handleAutoScroll(clientY);
    };
    
    // Добавляем глобальный обработчик
    document.addEventListener('dragover', handleGlobalDragOver);
    
    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
    };
  }, [isDragging, handleAutoScroll]);

  // Очистка таймаутов при размонтировании
  // ВАЖНО: Должен быть объявлен ДО условных возвратов
  useEffect(() => {
    return () => {
      if (dropTimeoutRef.current) {
        clearTimeout(dropTimeoutRef.current);
      }
      
      // Очищаем все таймеры debounce для ссылок на постов
      Object.values(postLinkUpdateTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
      postLinkUpdateTimeouts.current = {};
      
      // Очищаем тайм-аут ожидания подтверждения от сервера
      if (pendingMoveTimeoutRef.current) {
        clearTimeout(pendingMoveTimeoutRef.current);
        pendingMoveTimeoutRef.current = null;
      }
      
      // Останавливаем автоматическую прокрутку при размонтировании
      stopAutoScroll();
    };
  }, [stopAutoScroll]);

  // Отслеживание завершения задачи генерации недели через SignalR
  // ВАЖНО: Должен быть объявлен ДО условных возвратов
  useEffect(() => {
    if (!weekGenerationTaskId) return;
    
    const signalRService = tasksSignalRServiceRef?.current;
    
    if (!signalRService) return;
    
    // Подписываемся на события завершения задач через SignalR
    const unsubscribeCompleted = signalRService.onTaskCompleted((task: any) => {
      if (task.id === weekGenerationTaskId || task.taskId === weekGenerationTaskId) {
        console.log('✅ [WEEK] Генерация недели завершена через SignalR');
        // Сбрасываем состояние генерации
        setWeekGenerationState(false, null, null);
        
        // Инвалидируем кэш для загрузки новых данных
        if (contentPlanId) {
          // Используем setTimeout для гарантии, что данные обновились на сервере
          setTimeout(() => {
            invalidateContentPlanData(contentPlanId);
            // Принудительно обновляем кэш React Query
            queryClient.invalidateQueries({ queryKey: ['contentPlan', contentPlanId] });
            queryClient.refetchQueries({ queryKey: ['contentPlan', contentPlanId] });
          }, 1000);
        }
      }
    });
    
    const unsubscribeError = signalRService.onTaskError((task: any) => {
      if (task.id === weekGenerationTaskId || task.taskId === weekGenerationTaskId) {
        console.log('❌ [WEEK] Ошибка генерации недели через SignalR');
        // Сбрасываем состояние генерации при ошибке
        setWeekGenerationState(false, null, null);
      }
    });
    
    const unsubscribeCancelled = signalRService.onTaskCancelled((task: any) => {
      if (task.id === weekGenerationTaskId || task.taskId === weekGenerationTaskId) {
        console.log('🚫 [WEEK] Генерация недели отменена через SignalR');
        // Сбрасываем состояние генерации при отмене
        setWeekGenerationState(false, null, null);
      }
    });
    
    return () => {
      unsubscribeCompleted();
      unsubscribeError();
      unsubscribeCancelled();
    };
  }, [weekGenerationTaskId, setWeekGenerationState, invalidateContentPlanData, contentPlanId, tasksSignalRServiceRef, queryClient]);
  
  if (!localPlanData) {
    return <div className="text-center p-8">Нет данных для отображения контент-плана.</div>;
  }
  
  // Если после нормализации данные всё ещё отсутствуют
  if (!normalizedPlanData) {
    return <div className="text-center p-8">Некорректный формат данных контент-плана.</div>;
  }

  const days = normalizedPlanData.days || [];
  const weeks = normalizedPlanData.weeks || []; // Для обратной совместимости
  

  
  const handlePostClick = (post: PostData) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  // Функция для создания нового поста со значениями по умолчанию
  const handleAddPost = (dayDate: string, dayName: string) => {
    // Определяем платформу и формат по умолчанию на основе настроек плана
    let defaultPlatform = 'Instagram';
    let defaultFormat = 'Post';

    // Выбираем платформу на основе настроек контент-плана
    const socialNetworks = normalizedPlanData?.originalRequest?.socialNetworks || normalizedPlanData?.socialNetworksSelected;
    const instagramFormats = normalizedPlanData?.originalRequest?.instagramFormats || normalizedPlanData?.instagramFormatsUsed;
    
    if (socialNetworks?.instagram) {
      defaultPlatform = 'Instagram';
      // Определяем формат на основе доступных форматов Instagram
      if (instagramFormats?.reels) {
        defaultFormat = 'Reels';
      } else if (instagramFormats?.carousel) {
        defaultFormat = 'Carousel';
      } else if (instagramFormats?.post) {
        defaultFormat = 'Post';
      }
    } else if (socialNetworks?.telegram) {
      defaultPlatform = 'Telegram';
      defaultFormat = 'Post';
    }

    const newPost: PostData = {
      // Временный ID для нового поста (будет заменен сервером)
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      platform: defaultPlatform,
      format: defaultFormat,
      title: '',
      idea: '',
      content: '',
      date: dayDate
    };

    setSelectedPost(newPost);
    setIsModalOpen(true);
  };
  
  const handleSavePost = async (updatedPost: PostData, skipLocalUpdate = false) => {

    
    if (!contentPlanId) {
      console.error('Нет ID контент-плана для сохранения:', { contentPlanId });
      return;
    }

    const isNewPost = !updatedPost.id || updatedPost.id.startsWith('temp_');
    
    try {
      if (isNewPost) {
        // Это новый пост - создаем его


        const createRequest: UpdatePostRequest = {
          contentPlanId,
          // Не передаем postId для новых постов
          postData: {
            platform: updatedPost.platform,
            format: updatedPost.format,
            title: updatedPost.title,
            idea: updatedPost.idea,
            content: updatedPost.content,
            carouselHtmlJson: (updatedPost as any).carouselHtmlJson,
            date: updatedPost.date,
            coverStyleId: updatedPost.coverStyle ? parseInt(updatedPost.coverStyle.toString()) : undefined,
            coverImage: updatedPost.coverImage,
            links: updatedPost.links || [],
            isReelsShot: updatedPost.isReelsShot || false,
            postLink: updatedPost.postLink
          }
        };
        
        
        // Вызываем API для создания поста (без postId)
        const response = await contentApi.updatePost(createRequest);
        
        
        // Получаем ID нового поста из ответа сервера
        let newPostId = updatedPost.id; // используем временный ID как fallback
        
        if (response && response.data) {
          // Проверяем различные возможные поля для ID
          newPostId = response.data.id || response.data.Id || response.data.postId || response.data.PostId || newPostId;
        }
        
        // Если ID изменился с временного на реальный, обновляем сообщения чата
        if (newPostId !== updatedPost.id && updatedPost.id && updatedPost.id.startsWith('temp_') && contentPlanId && newPostId) {
          try {
            // Импортируем PostChatService для обновления сообщений
            const { PostChatService } = await import('../services/postChatService');
            const chatService = new PostChatService();
            
            // Подключаемся и обновляем PostId в сообщениях
            await chatService.connect();
            // Проверяем что все ID существуют и являются строками
            if (updatedPost.id && typeof updatedPost.id === 'string' && 
                newPostId && typeof newPostId === 'string' && 
                updatedPost.id.startsWith('temp_')) {
              await chatService.updatePostIdInMessages(contentPlanId, updatedPost.id, newPostId);
            }
            await chatService.disconnect();
            
          } catch (error) {
            console.warn('Не удалось обновить PostId в сообщениях чата:', error);
          }
        }
        
        // Добавляем новый пост в локальные данные с ID от сервера
        const newPostWithId = { ...updatedPost, id: newPostId };
        addLocalPost(newPostWithId, updatedPost.date || '');
        
        // Инвалидируем кэш после создания поста
        console.log('💾 [POST] Новый пост создан, вызываем инвалидацию кэша');
        invalidateContentPlanData(contentPlanId);
        
      } else {
        // Это существующий пост - обновляем его
        
        // Подготавливаем данные для API запроса
        const updateRequest: UpdatePostRequest = {
          contentPlanId,
          postId: updatedPost.id,
          postData: {
            platform: updatedPost.platform,
            format: updatedPost.format,
            title: updatedPost.title,
            idea: updatedPost.idea,
            content: updatedPost.content,
            carouselHtmlJson: (updatedPost as any).carouselHtmlJson,
            date: updatedPost.date,
            coverStyleId: updatedPost.coverStyle ? parseInt(updatedPost.coverStyle.toString()) : undefined,
            coverImage: updatedPost.coverImage,
            links: updatedPost.links || [],
            isReelsShot: updatedPost.isReelsShot || false,
            postLink: updatedPost.postLink
          }
        };
        

        // Вызываем API для обновления поста
        const response = await contentApi.updatePost(updateRequest);
        
        
        // Обновляем локальные данные только если это не было сделано optimistically
        if (!skipLocalUpdate) {

          updateLocalPost(updatedPost);
        } else {
        }
        
        // Инвалидируем кэш после обновления поста
        console.log('💾 [POST] Пост обновлен, вызываем инвалидацию кэша');
        invalidateContentPlanData(contentPlanId);
        
      }
      
    } catch (error) {
    }
  };

  // Функция для обработки удаления поста
  const handleDeletePost = async (post: PostData) => {
    
    if (!contentPlanId || !post.id) {
      console.error('Нет ID контент-плана или ID поста для удаления:', { contentPlanId, postId: post.id });
      return;
    }

    setIsDeleting(true);
    
    try {
      const deleteRequest: DeletePostRequest = {
        contentPlanId,
        postId: post.id
      };
      
      
      // Вызываем API для удаления поста
      const response = await contentApi.deletePost(deleteRequest);
      
      
      // Удаляем пост из локальных данных
      removeLocalPost(post.id);
      
      // Инвалидируем кэш после удаления поста
      console.log('🗑️ [POST] Пост удален, вызываем инвалидацию кэша');
      invalidateContentPlanData(contentPlanId);
      
      // Закрываем модальное окно
      setIsDeleteModalOpen(false);
      setPostToDelete(null);
      
    } catch (error) {
    } finally {
      setIsDeleting(false);
    }
  };

  // Функция для открытия модального окна удаления
  const handleDeleteClick = (e: React.MouseEvent, post: PostData) => {
    e.stopPropagation(); // Предотвращаем открытие модального окна редактирования
    setPostToDelete(post);
    setIsDeleteModalOpen(true);
  };

  // Новые функции drag & drop
  const handleDragStart = (e: React.DragEvent, post: PostData, sourceDate: string) => {
    
    // Проверяем, что у нас есть необходимые данные
    if (!post.id || !sourceDate) {
      e.preventDefault();
      return;
    }
    
    setIsDragging(true);
    setDraggedPostId(post.id);
    setDraggedFromDate(sourceDate);
    
    
    // Устанавливаем данные для передачи
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      postId: post.id,
      sourceDate: sourceDate
    }));
    
    // Визуальный эффект
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '0.5';
  };
  

  const handleDragEnd = (e: React.DragEvent) => {
    
    setIsDragging(false);
    setDraggedPostId(null);
    setDraggedFromDate(null);
    setDropTargetDate(null);
    
    // Останавливаем автоматическую прокрутку
    stopAutoScroll();
    
    // Восстанавливаем стили
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
  };

  const handleDragOver = (e: React.DragEvent, targetDate: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (dropTargetDate !== targetDate) {
      setDropTargetDate(targetDate);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    // Проверяем, что мышь действительно покинула область
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDropTargetDate(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, targetDate: string) => {
    
    e.preventDefault();
    setDropTargetDate(null);
    
    // Очищаем предыдущий таймаут если он есть
    if (dropTimeoutRef.current) {
      clearTimeout(dropTimeoutRef.current);
    }
    
    // Предотвращаем множественные вызовы
    if (isMovingPost) {
      return;
    }
    
    if (!isDragging || !draggedPostId || !draggedFromDate) {
      return;
    }
    
    if (draggedFromDate === targetDate) {
      return;
    }
    
    if (!contentPlanId || !localPlanData) {
      return;
    }
    
    // Находим пост для перемещения
    let postToMove: PostData | null = null;
    let foundInWeeks = false;
    
    // Нормализуем дату для поиска
    const normalizedDraggedFromDate = normalizeDate(draggedFromDate);
    const normalizedTargetDate = normalizeDate(targetDate);
    

    // Сначала ищем в структуре weeks (обратная совместимость)
    if (localPlanData.weeks) {
      for (const week of localPlanData.weeks) {
        for (const day of week.days) {
          const normalizedDayDate = normalizeDate(day.date);
          if (normalizedDayDate === normalizedDraggedFromDate) {
            postToMove = day.posts.find(p => p.id === draggedPostId) || null;
            foundInWeeks = true;
            break;
          }
        }
        if (postToMove) break;
      }
    }
    
    // Если не нашли в weeks, ищем в новой структуре days
    if (!postToMove && localPlanData.days) {
      for (const day of localPlanData.days) {
        const normalizedDayDate = normalizeDate(day.date);
        if (normalizedDayDate === normalizedDraggedFromDate) {
          postToMove = day.posts.find(p => p.id === draggedPostId) || null;
          break;
        }
      }
    }
    

    
    // Сохраняем текущие данные для отката при ошибке
    const originalPlanData = localPlanData;
    
    try {
      // Немедленно обновляем локальные данные для отзывчивого UI
      let newPlanData;
      
      if (foundInWeeks && localPlanData.weeks) {
        // Обновляем структуру weeks
        newPlanData = {
          ...localPlanData,
          weeks: localPlanData.weeks.map(week => ({
            ...week,
            days: week.days.map(day => {
              const normalizedDayDate = normalizeDate(day.date);
              if (normalizedDayDate === normalizedDraggedFromDate) {
                // Удаляем пост из исходного дня
                return {
                  ...day,
                  posts: day.posts.filter(p => p.id !== draggedPostId)
                };
              } else if (normalizedDayDate === normalizedTargetDate) {
                // Добавляем пост в целевой день
                return {
                  ...day,
                  posts: [...day.posts, { 
                    ...postToMove!, 
                    date: targetDate,
                    platform: postToMove!.platform || '',
                    format: postToMove!.format || '',
                    title: postToMove!.title || '',
                    idea: postToMove!.idea || '',
                    content: postToMove!.content || ''
                  }]
                };
              }
              return day;
            })
          }))
        };
      } else if (localPlanData.days) {
        // Обновляем структуру days
        newPlanData = {
          ...localPlanData,
          days: localPlanData.days.map(day => {
            const normalizedDayDate = normalizeDate(day.date);
            if (normalizedDayDate === normalizedDraggedFromDate) {
              // Удаляем пост из исходного дня
              return {
                ...day,
                posts: day.posts.filter(p => p.id !== draggedPostId)
              };
            } else if (normalizedDayDate === normalizedTargetDate) {
              // Добавляем пост в целевой день
              return {
                ...day,
                posts: [...day.posts, { 
                  ...postToMove!, 
                  date: targetDate,
                  platform: postToMove!.platform || '',
                  format: postToMove!.format || '',
                  title: postToMove!.title || '',
                  idea: postToMove!.idea || '',
                  content: postToMove!.content || ''
                }]
              };
            }
            return day;
          })
        };
      } else {
        throw new Error('Не найдена структура данных для обновления');
      }
      
      setLocalPlanData(newPlanData);
      
      // Отправляем на сервер
      const serverDate = normalizedTargetDate;
      
      // Валидация данных перед отправкой
      if (!postToMove || !postToMove.platform || !postToMove.format || !postToMove.title) {
        throw new Error('Недостаточно данных поста для отправки на сервер');
      }
      
      setIsMovingPost(true);
      
      const updateRequest: UpdatePostRequest = {
        contentPlanId,
        postId: draggedPostId,
        postData: {
          platform: postToMove.platform,
          format: postToMove.format,
          title: postToMove.title,
          idea: postToMove.idea,
          content: postToMove.content,
          carouselHtmlJson: (postToMove as any).carouselHtmlJson,
          date: serverDate,
          coverStyleId: postToMove.coverStyleId,
          coverImage: postToMove.coverImage,
          links: postToMove.links || [],
          isReelsShot: postToMove.isReelsShot || false,
          postLink: postToMove.postLink
        }
      };
      
              // Ставим флаг ожидания подтверждения от сервера именно этой операции переноса
        const timestamp = Date.now();
        pendingMoveRef.current = { postId: draggedPostId, targetDate: serverDate, timestamp };
      
      
      // Устанавливаем тайм-аут на случай, если сервер не ответит
      pendingMoveTimeoutRef.current = setTimeout(() => {
        pendingMoveRef.current = null;
        pendingMoveTimeoutRef.current = null;
      }, 30000); // 30 секунд для медленного сервера
      
      await contentApi.updatePost(updateRequest);
      
      setIsMovingPost(false);
      
      // Инвалидируем кэш после успешного перемещения поста
      console.log('🔄 [POST] Пост перемещен, вызываем инвалидацию кэша');
      invalidateContentPlanData(contentPlanId);
      
      // Увеличиваем версию для принудительного обновления UI
      setDragDropVersion(prev => prev + 1);
      
    } catch (error) {
      
      // Определяем тип ошибки для более точного сообщения
      let errorMessage = 'Ошибка перемещения поста';
      if (error instanceof Error) {
        if (error.message.includes('Network')) {
          errorMessage = 'Ошибка сети. Проверьте подключение к интернету.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Пост не найден на сервере.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Недостаточно прав для перемещения поста.';
        }
      }
      
      // Восстанавливаем исходные данные при ошибке
      setLocalPlanData(originalPlanData);
      setLastServerData(originalPlanData);
      
      // Очищаем ожидание подтверждения при ошибке
      pendingMoveRef.current = null;
      if (pendingMoveTimeoutRef.current) {
        clearTimeout(pendingMoveTimeoutRef.current);
        pendingMoveTimeoutRef.current = null;
      }
    } finally {
      // Очищаем состояния drag & drop в любом случае
      setIsDragging(false);
      setDraggedPostId(null);
      setDraggedFromDate(null);
      setDropTargetDate(null);
      setIsMovingPost(false);
    }
  };

  const handleGeneratePost = async (dayDate: string, format?: string, reelsType?: string) => {
    
    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }
    
    if (!contentPlanId) {
      return;
    }

    let serverDate = dayDate;
    // The date is dd.MM.yyyy, convert to yyyy-MM-dd for the server
    if (dayDate.includes('.')) {
      const [day, month, year] = dayDate.split('.');
      serverDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    setGeneratingPostForDate(dayDate);
    try {
      const requestData: any = {
        contentPlanId,
        date: serverDate,
      };


      // Добавляем формат если он указан
      if (format) {
        // Приводим формат к правильному регистру как в существующих постах
        const formatMapping: Record<string, string> = {
          'reels': 'Reels',
          'carousel': 'Carousel', 
          'post': 'Post'
        };
        
        const normalizedFormat = formatMapping[format.toLowerCase()] || format;
        requestData.format = normalizedFormat;
      } else {
      }

      if (reelsType) {
        requestData.reelsType = reelsType;
      }

      const response = await contentApi.generatePost(requestData);

      if (response.data) {
        // Сохраняем ID задачи для отслеживания
        if ((response.data as any)?.taskId) {
          setPostGenerationTasks(prev => ({
            ...prev,
            [dayDate]: (response.data as any).taskId
          }));
          // Трекер задач берет на себя управление, поэтому убираем состояние загрузки кнопки
          setGeneratingPostForDate(null);
        }
  
        // Добавляем дату в правильном формате к посту
        const postWithDate = {
          ...response.data,
          date: dayDate // Используем исходный формат даты для корректного добавления в UI
        };
        
        
        addLocalPost(postWithDate, dayDate);

        // Если taskId не пришел, запускаем анимацию завершения
        if (!(response.data as any)?.taskId) {
          setCompletingPostGenerationForDate(dayDate);
        } else {
          // Если taskId пришел, трекер задач берет на себя управление, поэтому убираем состояние загрузки кнопки
          setGeneratingPostForDate(null);
        }
      } else {
        throw new Error('API did not return post data');
      }

    } catch (error) {
      console.error('Ошибка при генерации поста:', error);
      setGeneratingPostForDate(null);
    }
  };

  // Функция для обработки клика на кнопку генерации поста
  const handleGeneratePostClick = (dayDate: string) => {
    
    // Получаем доступные форматы из настроек контент-плана или существующих постов
    const availableFormats = getAvailableInstagramFormats;
    
    
    // Если найдено несколько форматов, показываем модальное окно выбора
    if (availableFormats.length > 1) {
      setFormatSelectionReelType('Экспертный');
      setSelectedDateForGeneration(dayDate);
      setIsFormatSelectionModalOpen(true);
      return;
    } else if (availableFormats.length === 1) {
      // Если доступен только один формат, используем его
      handleGeneratePost(dayDate, availableFormats[0], formatSelectionReelType);
      return;
    }
    
    // Если форматов нет, генерируем без указания формата
    handleGeneratePost(dayDate);
  };

  // Функция для обработки клика на кнопку генерации по идее
  const handleGenerateByIdeaClick = (dayDate: string) => {
    setSelectedDateForGeneration(dayDate);
    setIsIdeaGenerationModalOpen(true);
  };

  // Функция для генерации поста по идее автора
  const handleGeneratePostByIdea = async (authorIdea?: string, referencePostUrl?: string, reelsType?: string) => {
    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }
    
    if (!contentPlanId || !selectedDateForGeneration) {
      return;
    }

    const dayDate = selectedDateForGeneration;
    let serverDate = dayDate;
    
    // The date is dd.MM.yyyy, convert to yyyy-MM-dd for the server
    if (dayDate.includes('.')) {
      const [day, month, year] = dayDate.split('.');
      serverDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    setGeneratingIdeaForDate(dayDate);
    setIsIdeaGenerationModalOpen(false); // Закрываем модальное окно
    
    try {
      const requestData: GeneratePostByIdeaRequest = {
        contentPlanId,
        date: serverDate,
        authorIdea: authorIdea ?? '',
        referencePostUrl,
        reelsType
      };

      console.log('🎯 Генерация поста по идее:', requestData);

      const response = await contentApi.generatePostByIdea(requestData);

      if (response.data) {
        // Сохраняем ID задачи для отслеживания если есть
        if ((response.data as any)?.taskId) {
          setPostGenerationTasks(prev => ({
            ...prev,
            [dayDate]: (response.data as any).taskId
          }));
        }
  
        // Добавляем дату в правильном формате к посту
        const postWithDate = {
          ...response.data,
          date: dayDate, // Используем исходный формат даты для корректного добавления в UI
          format: 'Reels' // Всегда генерируем рилс
        };
        
        console.log('✅ Пост по идее сгенерирован:', postWithDate);
        
        addLocalPost(postWithDate, dayDate);

        // Если taskId не пришел, запускаем анимацию завершения
        if (!(response.data as any)?.taskId) {
          setCompletingPostGenerationForDate(dayDate);
        }
      } else {
        throw new Error('API не вернул данные поста');
      }

    } catch (error) {
      console.error('Ошибка при генерации поста по идее:', error);
    } finally {
      setGeneratingIdeaForDate(null);
      setSelectedDateForGeneration(null);
    }
  };

  // Функция для генерации новой недели
  const handleGenerateWeek = async (additionalInfo: string, enableDeepResearch?: boolean, reelsTemplateId?: string) => {
    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }
    
    if (!contentPlanId) {
      return;
    }


    
    const startTime = Date.now();
    setWeekGenerationState(true, startTime, null);
    setIsWeekGenerationModalOpen(false); // Закрываем модальное окно сразу при начале генерации
    
    try {
      
      const response = await contentApi.generateWeek({
        contentPlanId,
        additionalInfo,
        enableDeepResearch,
        reelsTemplateId
      });

      if (response.data && response.data.taskId) {
        setWeekGenerationState(true, startTime, response.data.taskId);
      } else {
        throw new Error('API не вернул taskId');
      }

    } catch (error) {
      setWeekGenerationState(false, null, null);
    }
  };

  const handleWeekGenerationComplete = () => {
    // Прогресс-бар будет скрыт автоматически когда придет результат через SignalR
    // Здесь можно добавить дополнительную логику если нужно
  };

  // Функция для изменения порядка сортировки
  const handleSortOrderChange = () => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);
  };

  // Обработчик выбора формата в модальном окне
  const handleFormatSelection = (format: string) => {
    if (selectedDateForGeneration) {
      setIsFormatSelectionModalOpen(false);
      handleGeneratePost(selectedDateForGeneration, format, formatSelectionReelType);
      setSelectedDateForGeneration(null);
    }
  };

  // Обработчик закрытия модального окна выбора формата
  const handleFormatSelectionClose = () => {
    setIsFormatSelectionModalOpen(false);
    setSelectedDateForGeneration(null);
  };
  
  return (
    <>
      {/* Компонент настроек контент-плана */}
      {contentPlanId && (
        <ContentPlanSettings 
          contentPlanId={contentPlanId}
          isVisible={isSettingsVisible}
          onToggle={() => setIsSettingsVisible(!isSettingsVisible)}
          initialSettings={(() => {
            const s = normalizedPlanData;
            if (!s) return null;
            const settings = {
              topic: s.topic,
              description: s.description,
              services: s.services,
              aboutAuthor: s.aboutAuthor,
              contentPreferences: s.contentPreferences || '',
              audienceArticleId: s.audienceArticleId ?? null,
              authorSocialHandle: s.authorSocialHandle || '',
              referenceSocialHandle: s.referenceSocialHandle || '',
              socialNetworks: s.socialNetworks,
              instagramFormats: s.instagramFormats,
              reelsComplexity: s.reelsComplexity,
              frequency: s.frequency,
              duration: s.duration
            };
            return settings;
          })()}
        />
      )}
      
      {/* Встроенный рендер контент-плана */}
      <div className="pb-3 sm:pb-4 lg:pb-6 rounded-lg">
          {/* Кнопки для генерации новой недели */}
          {!isGeneratingWeek && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
              <button
                onClick={() => setIsWeekGenerationModalOpen(true)}
                disabled={isAnyPostGenerating}
                className="px-6 py-3 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/50 dark:hover:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-5 h-5" />
                <span>Сгенерировать новую неделю</span>
              </button>
            </div>
          )}

          {/* Прогресс-бар генерации новой недели */}
          {isGeneratingWeek && weekGenerationStartTime && (
            <div className="max-w-md mx-auto mb-4">
              <div className="space-y-3">
                <div className="flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
                  <Sparkles className="w-5 h-5 animate-pulse" />
                  <span className="font-medium text-sm">Генерируем новую неделю...</span>
                </div>
                <TaskProgressBar 
                    taskType="ContentPlan"
                    startTime={weekGenerationStartTime}
                    progress={weekGenerationProgress}
                    className="w-full"
                />
                <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                    Анализируем контекст, генерируем идеи и создаем посты для новой недели
                    </p>
                </div>
              </div>
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => {
                    if (weekGenerationTaskId) {
                      cancelTask(weekGenerationTaskId);
                    }
                    setWeekGenerationState(false, null, null);
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors flex items-center space-x-2"
                >
                  <X className="w-4 h-4" />
                  <span>Отменить</span>
                </button>
              </div>
            </div>
          )}

          {/* Кнопки сортировки и скрытия прошедших дней */}
          <div className="flex justify-left mb-4 gap-2">
            <button
              onClick={handleSortOrderChange}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors flex items-center space-x-2 border border-gray-300 dark:border-gray-600"
              title={`Сортировка по дате: ${sortOrder === 'asc' ? 'сначала новые' : 'сначала старые'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 dark:text-gray-400">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              <span className="text-sm">
                {sortOrder === 'asc' ? 'Сначала новые' : 'Сначала старые'}
              </span>
              {sortOrder === 'asc' ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 dark:text-gray-400">
                  <path d="M7 13l3 3 3-3"></path>
                  <path d="M7 6l3 3 3-3"></path>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 dark:text-gray-400">
                  <path d="M17 11l-3-3-3 3"></path>
                  <path d="M17 18l-3-3-3 3"></path>
                </svg>
              )}
            </button>
            <button
              onClick={() => setHidePastDays(v => !v)}
              className={`${hidePastDays ? 'bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:hover:bg-blue-900/60 dark:text-blue-300 dark:border-blue-700' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 dark:border-gray-600'} px-4 py-2 font-medium rounded-lg transition-colors flex items-center space-x-2 border`}
              title={hidePastDays ? 'Показать прошедшие дни' : 'Скрыть прошедшие дни'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={hidePastDays ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span className="text-sm">{hidePastDays ? 'Прошедшие: Вкл' : 'Прошедшие: Выкл'}</span>
            </button>
          </div>
          
          {isMovingPost && (
            <div className="mt-4 flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 dark:border-blue-400"></div>
              <span className="text-sm font-medium">Перемещение поста...</span>
            </div>
          )}
          
        </div>

        {/* Гибкая сетка для дней */}
        {daysToRender.length > 0 ? (
          <div
            ref={containerRef}
            className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(290px,1fr))]"
          >
            {daysToRender.map((day, dayIndex) => {
              const now = new Date();
              const todayUtcMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
              const isTodayCard = (() => {
                const ms = parseDateToUtcMs(day.date);
                return ms !== null && ms === todayUtcMs;
              })();
              const isTrulyGenerating = generatingPostForDate === day.date || !!postGenerationTasks[day.date];
              const isCompleting = completingPostGenerationForDate === day.date;
              const isLoading = isTrulyGenerating || isCompleting;
              return (
              <div 
                key={day.date} 
                className={`relative flex flex-col min-h-[340px] p-4 transition-all duration-300 bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-2 ${
                  isTodayCard
                    ? 'border-2 border-blue-200 dark:border-blue-400/30 ring-2 ring-blue-400/20' 
                    : 'border-2 '
                } ${
                  dropTargetDate === day.date 
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 scale-105' 
                    : isDragging && !isTodayCard
                      ? 'border-dashed border-gray-300 dark:border-gray-600' 
                      : !isTodayCard
                        ? 'border-gray-200 dark:border-gray-700'
                        : ''
                }`}
                onDragOver={(e) => handleDragOver(e, day.date)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, day.date)}
              >
                {isTodayCard && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full bg-purple-600 text-white text-xs shadow-md border-2 border-white dark:border-gray-800">
                    Сегодня
                  </div>
                )}
                {/* Заголовок дня */}
                <div className="text-center mb-3 pb-3 border-b-2 border-gray-100 dark:border-gray-700">
                  <div className="text-xl font-bold text-gray-900 dark:text-white w-full min-h-[3rem] flex items-center justify-center break-words leading-tight">
                    {day.dayName}
                  </div>
                  <div className={`text-lg mt-1 font-semibold ${isTodayCard ? 'text-purple-600 dark:text-purple-400' : 'text-blue-600 dark:text-blue-400'}`}>
                    {day.date}
                  </div>
                </div>
                  
                {/* Посты */}
                <div className="space-y-4 flex-grow">
                  {day.posts && day.posts.length > 0 ? (
                    day.posts.map((post, postIndex) => {
                      return (
                      <div 
                        key={post.id || postIndex} 
                        className={`bg-gray-100 sm:bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-200 sm:hover:bg-gray-100 dark:hover:bg-gray-700 p-3 rounded-xl cursor-pointer transition-all duration-200 relative group ${
                          isDragging && draggedPostId === post.id 
                            ? 'opacity-50 scale-95 ring-2 ring-blue-400' 
                            : ''
                        }`}
                        draggable="true"
                        onDragStart={(e) => handleDragStart(e, post, day.date)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => {
                          // Предотвращаем открытие модального окна если идет drag & drop
                          if (isDragging) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                          }
                          handlePostClick(post);
                        }}
                      >
                        
                        {/* Галочка для всех постов Instagram (Reels, Carousel, Post) - наверх слева от кнопки удаления */}
                        {post.platform?.toLowerCase() === 'instagram' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              
                              const updatedPost = {
                                ...post,
                                isReelsShot: !post.isReelsShot,
                                postLink: post.isReelsShot ? undefined : post.postLink
                              };
                              
                              // 🚀 Optimistic update - сначала обновляем локально
                              updateLocalPost(updatedPost);
                              
                              // 🌐 Затем отправляем на сервер с флагом skipLocalUpdate
                              handleSavePost(updatedPost, true);
                            }}
                            className={`hidden absolute top-3 right-12 p-1 rounded-md transition-all duration-200 z-10 ${
                              post.isReelsShot
                                ? 'text-blue-600 dark:text-blue-400 opacity-100' // Отмеченная - всегда видна
                                : 'text-gray-400 dark:text-gray-500 opacity-100 sm:opacity-0 sm:group-hover:opacity-100' // Не отмеченная - всегда видна на мобайле, только при hover на десктопе
                            } hover:text-blue-500 dark:hover:text-blue-400`}
                            title={post.isReelsShot ? (post.format?.toLowerCase() === 'reels' ? 'Рилс снят' : post.format?.toLowerCase() === 'carousel' ? 'Карусель опубликована' : 'Пост опубликован') : (post.format?.toLowerCase() === 'reels' ? 'Отметить как снятый' : post.format?.toLowerCase() === 'carousel' ? 'Отметить как опубликованную' : 'Отметить как опубликованный')}
                          >
                            <Check className="w-6 h-6" />
                          </button>
                        )}

                        {/* Кнопка удаления */}
                        <button
                          onClick={(e) => handleDeleteClick(e, post)}
                          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-gray-300 dark:bg-gray-600 hover:bg-red-500 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200 flex items-center justify-center z-10"
                          title="Удалить пост"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                        
                        {/* Иконка перетаскивания и информация о посте */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            {/* Иконка перетаскивания (три полоски) */}
                            <div 
                              className="cursor-grab active:cursor-grabbing text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded"
                              title="Перетащите для перемещения поста"
                            >
                              {post.isReelsShot ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600 dark:text-emerald-500">
                                  <rect x="3" y="3" width="18" height="18" rx="5" ry="5"></rect>
                                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                                  <circle cx="17.5" cy="6.5" r="0.5"></circle>
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="3" y1="6" x2="21" y2="6"></line>
                                  <line x1="3" y1="12" x2="21" y2="12"></line>
                                  <line x1="3" y1="18" x2="21" y2="18"></line>
                                </svg>
                              )}
                            </div>
                            
                            {/* Платформа и формат */}
                            <div className="flex flex-col">
                                {(() => {
                                  const fmt = (post.format || '').toLowerCase();
                                  if (fmt === 'reels') return <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-800/50 text-green-700 dark:text-green-300 rounded-full font-medium">Рилс</span>;
                                  if (fmt === 'post') return <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 rounded-full font-medium">Пост</span>;
                                  if (fmt === 'carousel') return <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300 rounded-full font-medium">Карусель</span>;
                                  return <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full font-medium">{post.format}</span>;
                                })()}
                            </div>
                          </div>
                        </div>
                        
                        {/* Заголовок поста */}
                        {post.title && (
                            <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200 line-clamp-3 mb-1">
                                {post.title}
                            </h4>
                        )}

                        {/* Идея поста */}
                        {post.idea && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 mb-4">
                            {post.idea}
                          </p>
                        )}
                        
                        {/* Обложка */}
                        {post.coverImage && (
                          <div className="mt-2 bg-gray-200 dark:bg-gray-600 rounded-lg overflow-hidden h-28">
                            <img src={post.coverImage} alt="Обложка" className="w-full h-full object-cover" />
                          </div>
                        )}

                        {/* Поле для ссылки на пост - появляется снизу при отмеченной галочке для всех постов Instagram (Reels, Carousel, Post) */}
                        {post.platform?.toLowerCase() === 'instagram' && post.isReelsShot && (
                          <div className="mt-2">
                            {!editingPostLinks[post.id!] ? (
                              // Режим просмотра - показываем ссылку как текст без границ
                              <div>
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    
                                    setEditingPostLinks(prev => ({
                                      ...prev,
                                      [post.id!]: true
                                    }));
                                    // Устанавливаем текущее значение в поле редактирования
                                    setPostLinkInputValues(prev => ({
                                      ...prev,
                                      [post.id!]: post.postLink || ''
                                    }));
                                    // Очищаем ошибку при начале редактирования
                                    setPostLinkErrors(prev => {
                                      const newErrors = { ...prev };
                                      delete newErrors[post.id!];
                                      return newErrors;
                                    });
                                  }}
                                  className="w-full px-3 py-2 text-xs rounded-lg cursor-pointer hover:bg-gray-50 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 transition-colors min-h-[38px] flex items-center break-words overflow-wrap-anywhere word-break break-all overflow-hidden"
                                  title={`Нажмите для редактирования ссылки на ${post.format?.toLowerCase() === 'reels' ? 'рилс' : post.format?.toLowerCase() === 'carousel' ? 'карусель' : 'пост'}`}
                                >
                                  {(() => {
                                    // Показываем postLinkInputValues только если он определен (есть локальные изменения)
                                    // При ошибке валидации некорректное значение автоматически очищается при потере фокуса
                                    const displayValue = postLinkInputValues[post.id!] !== undefined 
                                      ? postLinkInputValues[post.id!] 
                                      : post.postLink;
                                      
                                    return displayValue || (
                                      <span className="text-gray-400 dark:text-gray-500">{getPostLinkPlaceholder(post.platform, post.format)}</span>
                                    );
                                  })()}
                                </div>
                                {/* Отображение ошибки валидации */}
                                {postLinkErrors[post.id!] && (
                                  <div className="mt-1 text-xs text-red-500 dark:text-red-400">
                                    {postLinkErrors[post.id!]}
                                  </div>
                                )}
                              </div>
                            ) : (
                              // Режим редактирования - показываем input с валидацией
                              <div>
                                <input
                                  type="url"
                                  value={postLinkInputValues[post.id!] !== undefined ? postLinkInputValues[post.id!] : (post.postLink || '')}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    const value = e.target.value;
                                    handlePostLinkChange(post, value);
                                  }}
                                  onBlur={() => {
                                    // Проверяем есть ли ошибка валидации
                                    if (postLinkErrors[post.id!]) {
                                      // Если есть ошибка, сбрасываем значение
                                      setPostLinkInputValues(prev => {
                                        const newValues = { ...prev };
                                        delete newValues[post.id!]; // Удаляем некорректное значение
                                        return newValues;
                                      });
                                      // Очищаем ошибку
                                      setPostLinkErrors(prev => {
                                        const newErrors = { ...prev };
                                        delete newErrors[post.id!];
                                        return newErrors;
                                      });
                                    }
                                    // Выходим из режима редактирования при потере фокуса
                                    setEditingPostLinks(prev => ({
                                      ...prev,
                                      [post.id!]: false
                                    }));
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === 'Escape') {
                                      e.stopPropagation();
                                      // При Escape всегда сбрасываем изменения, при Enter - только если есть ошибка
                                      if (e.key === 'Escape' || postLinkErrors[post.id!]) {
                                        // Сбрасываем значение на оригинальное
                                        setPostLinkInputValues(prev => {
                                          const newValues = { ...prev };
                                          delete newValues[post.id!];
                                          return newValues;
                                        });
                                        // Очищаем ошибку
                                        setPostLinkErrors(prev => {
                                          const newErrors = { ...prev };
                                          delete newErrors[post.id!];
                                          return newErrors;
                                        });
                                      }
                                      setEditingPostLinks(prev => ({
                                        ...prev,
                                        [post.id!]: false
                                      }));
                                    }
                                  }}
                                  placeholder={getPostLinkPlaceholder(post.platform, post.format)}
                                  className={`w-full px-3 py-2 text-xs border rounded-lg focus:ring-2 dark:bg-gray-800 dark:text-white transition-colors break-words overflow-wrap-anywhere word-break break-all overflow-hidden ${
                                    postLinkErrors[post.id!] 
                                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500 dark:border-red-600 dark:focus:ring-red-600 dark:focus:border-red-600' 
                                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:border-gray-600 dark:focus:ring-blue-600 dark:focus:border-blue-600'
                                  } dark:placeholder-gray-400`}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                />
                                {/* Отображение ошибки валидации под input */}
                                {postLinkErrors[post.id!] && (
                                  <div className="mt-1 text-xs text-red-500 dark:text-red-400">
                                    {postLinkErrors[post.id!]}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                    })
                  ) : (
                    <div className="text-center flex items-center justify-center flex-grow py-8">
                      <p className="text-base text-gray-500 dark:text-gray-400">
                        {dropTargetDate === day.date ? 'Отпустите здесь для перемещения' : 'Нет постов'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Кнопки генерации постов */}
                <div className="w-full mt-4 flex items-center justify-center space-x-2">
                  <button
                    onClick={() => handleGenerateByIdeaClick(day.date)}
                    disabled={isAnyPostGenerating}
                    className="flex-1 py-3 px-4 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/50 dark:hover:bg-blue-900 text-blue-700 dark:text-blue-300 font-bold rounded-lg transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                    title="Сгенерировать рилс по своей идее"
                  >
                    {(generatingIdeaForDate === day.date || postGenerationTasks[day.date]) && (
                      <ProgressBar 
                        key={`idea-progress-${day.date}-${postGenerationTasks[day.date] || 'local'}`}
                        taskId={postGenerationTasks[day.date]}
                        isComplete={completingPostGenerationForDate === day.date}
                        onAnimationComplete={() => {
                          setGeneratingPostForDate(null);
                          setCompletingPostGenerationForDate(null);
                        }}
                      />
                    )}
                    {(() => {
                      const isTrulyGeneratingIdea = (generatingIdeaForDate === day.date) || !!postGenerationTasks[day.date];
                      const isCompleting = completingPostGenerationForDate === day.date;
                      return (
                        <div className="relative z-10 flex items-center justify-center h-full" style={{minHeight: '20px'}}>
                          {isTrulyGeneratingIdea ? (
                            <span key={currentIdeaPhraseIndex} className="text-[10px] sm:text-xs font-medium text-center leading-tight truncate px-1" style={{ animation: 'fadeIn 0.5s' }}>
                              {ideaGeneratingPhrases[currentIdeaPhraseIndex]}
                            </span>
                          ) : isCompleting ? (
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                            </svg>
                          )}
                        </div>
                      );
                    })()}
                  </button>
                  <button
                    onClick={() => handleGeneratePostClick(day.date)}
                    disabled={isAnyPostGenerating}
                    className="flex-1 py-3 px-4 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/50 dark:hover:bg-blue-900 text-blue-700 dark:text-blue-300 font-bold rounded-lg transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                    title={isLoading ? "Дождитесь завершения генерации текущего поста" : "Сгенерировать пост с помощью ИИ (~50 сек)"}
                  >
                    {/* Прогресс-бар заполнения */}
                    {isLoading && (
                      <ProgressBar 
                        key={`progress-${day.date}-${generatingPostForDate || postGenerationTasks[day.date]}`} 
                        taskId={postGenerationTasks[day.date]}
                        isComplete={completingPostGenerationForDate === day.date}
                        onAnimationComplete={() => {
                            setGeneratingPostForDate(null);
                            setCompletingPostGenerationForDate(null);
                        }}
                      />
                    )}
                    
                    {/* Контент кнопки */}
                    <div className="relative z-10 flex items-center justify-center h-full" style={{minHeight: '20px'}}>
                      {isTrulyGenerating ? (
                        <span key={currentPhraseIndex} className="text-[10px] sm:text-xs font-medium text-center leading-tight truncate px-1" style={{ animation: 'fadeIn 0.5s' }}>
                          {generatingPhrases[currentPhraseIndex]}
                        </span>
                      ) : isCompleting ? (
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 814 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <Sparkles size={20} />
                      )}
                    </div>
                  </button>
                </div>
              </div>
            );
            })}
            
            {/* Кнопка добавления нового дня как карточка */}
            {contentPlanId && (
              <AddNewDayButton 
                contentPlanId={contentPlanId}
                onDayAdded={() => {
                  // Дополнительные действия после добавления дня (если нужны)
                  console.log('📅 [ADD-DAY] День добавлен, выполняем дополнительные действия');
                }}
                disabled={isAnyPostGenerating}
                lastDayDate={(() => {
                  // Находим последний день в списке
                  if (daysToRender.length === 0) return undefined;
                  
                  // Сортируем дни по дате, чтобы найти последний
                  const sortedDays = [...daysToRender].sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    return dateA.getTime() - dateB.getTime();
                  });
                  
                  const lastDay = sortedDays[sortedDays.length - 1];
                  
                  // Конвертируем дату в формат YYYY-MM-DD если нужно
                  if (lastDay.date.includes('.')) {
                    const [day, month, year] = lastDay.date.split('.');
                    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                  }
                  
                  return lastDay.date;
                })()}
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-gray-500 dark:text-gray-400">Нет данных для отображения.</p>
          </div>
        )}
      
      
      <ContentModal 
        isOpen={isModalOpen} 
        post={selectedPost} 
        onClose={() => setIsModalOpen(false)} 
        onSave={handleSavePost}
        contentPlanId={contentPlanId}
      />
      
      {/* Модальное окно подтверждения удаления */}
      <DeleteConfirmationModal 
        isOpen={isDeleteModalOpen} 
        post={postToDelete} 
        onClose={() => setIsDeleteModalOpen(false)} 
        onConfirm={() => postToDelete && handleDeletePost(postToDelete)} 
        isDeleting={isDeleting}
      />
      
      {/* Модальное окно генерации новой недели */}
      <WeekGenerationModal 
        isOpen={isWeekGenerationModalOpen} 
        onClose={() => setIsWeekGenerationModalOpen(false)} 
        onGenerate={handleGenerateWeek}
        isGenerating={isGeneratingWeek}
      />
      
      {/* Модальное окно выбора формата поста */}
      <FormatSelectionModal 
        isOpen={isFormatSelectionModalOpen} 
        onClose={handleFormatSelectionClose} 
        onFormatSelect={handleFormatSelection}
        availableFormats={getAvailableInstagramFormats}
        reelType={formatSelectionReelType}
        onReelTypeChange={setFormatSelectionReelType}
      />
      
      {/* Модальное окно генерации по идее */}
      <IdeaGenerationModal 
        isOpen={isIdeaGenerationModalOpen} 
        onClose={() => setIsIdeaGenerationModalOpen(false)} 
        onGenerate={handleGeneratePostByIdea}
        isGenerating={generatingIdeaForDate !== null}
      />
    </>
  );
};

// Интерфейс для модального окна подтверждения удаления
interface DeleteConfirmationModalProps {
  isOpen: boolean;
  post: PostData | null;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

// Компонент модального окна подтверждения удаления
const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ 
  isOpen, 
  post, 
  onClose, 
  onConfirm, 
  isDeleting 
}) => {
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  
  // Создаём корневой элемент для портала
  useEffect(() => {
    let root = document.getElementById('modal-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'modal-root';
      document.body.appendChild(root);
    }
    setModalRoot(root);
    
    return () => {
      if (root && !root.hasChildNodes() && root.parentElement) {
        root.parentElement.removeChild(root);
      }
    };
  }, []);

  if (!isOpen || !post || !modalRoot) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-auto p-6"
        onClick={e => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600 dark:text-red-400">
              <path d="M3 6h18"></path>
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
              <path d="M8 6V4c0-1 1-2 2-2h4c-1 0 2 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Удалить пост?
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Это действие нельзя отменить
            </p>
          </div>
        </div>

        {/* Информация о посте */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {post.platform} - {post.format}
            </span>
          </div>
          {post.idea && (
            <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
              {post.idea}
            </p>
          )}
        </div>

        {/* Кнопки */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Отмена
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isDeleting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Удаление...
              </>
            ) : (
              'Удалить'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, modalRoot);
};